import { useEffect, useMemo, useState, Fragment } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../config";
import { usePayrollEnabled } from "../../utils/payrollFeature";
import PayrollReport from "./PayrollReport";
import s from "./ReportsPage.module.css";
import { exportFinanceDetailPdf } from "../../utils/financeDetailPdf";

const RANGES = [
  { value: "today",  label: "Today" },
  { value: "month",  label: "This month" },
  { value: "7",      label: "Last 7 days" },
  { value: "30",     label: "Last 30 days" },
  { value: "90",     label: "Last 90 days" },
  { value: "365",    label: "Last 12 months" },
  { value: "all",    label: "All time" },
  { value: "custom", label: "Custom range…" },
];

/** Friendly "May 1 → May 10, 2026" label for a custom-range pair.
 *  Falls back to "Custom range" when either side is missing so the
 *  CSV / report header still reads as something meaningful. */
function customRangeLabel(fromIso, toIso) {
  const fmt = (iso) => {
    const t = isoDateToStart(iso);
    if (Number.isNaN(t)) return null;
    return new Date(t).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  };
  const a = fmt(fromIso);
  const b = fmt(toIso);
  if (a && b) return `${a} → ${b}`;
  if (a)      return `From ${a}`;
  if (b)      return `Until ${b}`;
  return "Custom range";
}

/** Today's date as YYYY-MM-DD, with an optional day offset. Used to
 *  seed the custom-range pickers so the first switch to Custom mode
 *  opens on a sensible 30-day window instead of empty fields. */
function isoToday(dayOffset = 0) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (dayOffset) d.setDate(d.getDate() + dayOffset);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/** "YYYY-MM-DD" → start-of-day epoch ms (local timezone). Anchors at
 *  midnight so a custom From date includes payments stamped at any
 *  hour on that date. Invalid strings return NaN so the caller can
 *  fall back to a sane default. */
function isoDateToStart(iso) {
  if (!iso || typeof iso !== "string") return NaN;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return NaN;
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}

/** Same as `isoDateToStart` but returns the start of the FOLLOWING day,
 *  so callers can use a strict-less-than upper bound to make the
 *  "To" date inclusive (a payment at 2026-05-10 23:59 still lands in
 *  a "May 1 → May 10" window). */
function isoDateToEnd(iso) {
  const t = isoDateToStart(iso);
  if (Number.isNaN(t)) return NaN;
  return t + 24 * 60 * 60 * 1000;
}

/** Convert a window choice into a {from, to} pair of epoch-ms bounds.
 *  Filters use `t >= from && t < to`. "all" → [0, +∞) so nothing is
 *  filtered; "today" / rolling presets pin `from`; "custom" reads the
 *  user-picked From/To dates. Invalid custom dates fall back to the
 *  unbounded side so a half-filled custom range still produces sane
 *  output (e.g. "from May 1, no to-date" returns everything since
 *  May 1). */
function computeBounds(range, customFrom, customTo) {
  if (range === "all") return { from: 0, to: Infinity };
  if (range === "today") {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return { from: d.getTime(), to: Infinity };
  }
  if (range === "month") {
    // Calendar month to date — from the 1st at local midnight up to (but
    // not including) the 1st of next month. On the 1st itself this
    // correctly narrows to just that single day, same as "Today" would.
    const d = new Date();
    d.setDate(1); d.setHours(0, 0, 0, 0);
    const from = d.getTime();
    const to = new Date(d.getFullYear(), d.getMonth() + 1, 1, 0, 0, 0, 0).getTime();
    return { from, to };
  }
  if (range === "custom") {
    const from = isoDateToStart(customFrom);
    const to   = isoDateToEnd(customTo);
    return {
      from: Number.isNaN(from) ? 0       : from,
      to:   Number.isNaN(to)   ? Infinity : to,
    };
  }
  // Rolling N-day window — same behaviour as the old computeCutoff.
  const from = Date.now() - Number(range) * 24 * 60 * 60 * 1000;
  return { from, to: Infinity };
}

function money(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: "currency", currency: "PHP", maximumFractionDigits: 2 });
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// Small palette for initial-letter avatars in the method → student table.
// Hash the name into an index so the same student always lands on the same
// color across renders.
const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a","#8a6d28"];
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initial(name) {
  if (!name) return "?";
  const c = name.trim().charAt(0);
  return c ? c.toUpperCase() : "?";
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${date}, ${time}`;
}

// Returns the ms timestamp to use for date-range filtering.
// field="received" → paymentReceivedOn (cashier-entered receipt date), falling
// back to paidAt when the field is absent (legacy records).
// field="entered"  → paidAt (server-side timestamp when the record was created).
function paymentTsByField(p, field) {
  if (field === "entered") return new Date(p.paidAt || 0).getTime();
  if (p.paymentReceivedOn) return isoDateToStart(p.paymentReceivedOn);
  return new Date(p.paidAt || 0).getTime();
}

function effectivePaymentTs(p) {
  return paymentTsByField(p, "received");
}

/** Same idea as paymentTsByField, but for expenses: "received" (default)
 *  uses expenseDate — the day the money actually went out — falling back
 *  to addedAt (when the record was typed into the system) for the rare
 *  row missing an expenseDate. "entered" pins to addedAt outright, same
 *  as the payment side of this same Filter Date By toggle. */
function expenseTsByField(e, field) {
  if (field === "entered") return new Date(e.addedAt || 0).getTime();
  if (e.expenseDate) return isoDateToStart(e.expenseDate);
  return new Date(e.addedAt || 0).getTime();
}

// Human-readable date label using the same effective-date priority.
// paymentReceivedOn is a date-only value so we show just the date;
// paidAt is a full timestamp so we include the time (legacy fallback).
function effectivePaymentDateLabel(p) {
  if (p.paymentReceivedOn) return formatDate(p.paymentReceivedOn);
  return formatDateTime(p.paidAt);
}

function buildFovDetail(tileKey, rawData, kpis) {
  if (!rawData) return null;
  const { invoices = [], invoicesInRange = [], payments = [], allPayments = [],
          expenses = [], feeStructures = [], sections = [], students = [], gradeLevels = [] } = rawData;

  const stuMap  = new Map(students.map(x => [String(x.id), x]));
  const secMap  = new Map(sections.map(x => [String(x.id), x]));
  const gradeMap = new Map(gradeLevels.map(x => [String(x.id), x]));
  const invMap  = new Map(invoices.map(x => [String(x.id), x]));
  const feeMap  = new Map();
  for (const fs of feeStructures) feeMap.set(`${fs.gradeLevelId}|${fs.schoolYearId}`, Number(fs.totalAmount || 0));

  const fmtDate = iso => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d) ? "—" : d.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" });
  };

  const stuName = stu => stu ? `${stu.firstName || ""} ${stu.lastName || ""}`.trim() || `#${stu.id}` : "—";
  const glOf = inv => {
    const stu = stuMap.get(String(inv.studentId));
    const sec = stu?.currentSectionId ? secMap.get(String(stu.currentSectionId)) : null;
    return sec?.gradeLevelId ? gradeMap.get(String(sec.gradeLevelId)) : null;
  };
  const feeOf = inv => {
    const stu = stuMap.get(String(inv.studentId));
    const sec = stu?.currentSectionId ? secMap.get(String(stu.currentSectionId)) : null;
    const key = sec?.gradeLevelId && inv.schoolYearId ? `${sec.gradeLevelId}|${inv.schoolYearId}` : null;
    return key ? feeMap.get(key) : null;
  };

  switch (tileKey) {
    case "grand": {
      const byGrade = new Map();
      for (const inv of invoicesInRange) {
        const gl    = glOf(inv);
        const fsAmt = feeOf(inv) ?? Number(inv.amount || 0);
        const id    = gl?.id ?? "unk";
        const lbl   = gl?.name ?? "Unclassified";
        if (!byGrade.has(id)) byGrade.set(id, { label: lbl, feeAmt: fsAmt, count: 0, subtotal: 0, students: [] });
        const r = byGrade.get(id); r.count++; r.subtotal += fsAmt; r.feeAmt = fsAmt;
        const stu = stuMap.get(String(inv.studentId));
        r.students.push({
          id: inv.studentId ?? `inv-${inv.id}`,
          name: stuName(stu),
          studentNumber: stu?.studentNumber || "",
          amount: fsAmt,
        });
      }
      return {
        type: "grand",
        rows: [...byGrade.values()]
          .map(r => ({ ...r, students: r.students.sort((a,b) => a.name.localeCompare(b.name)) }))
          .sort((a,b) => a.label.localeCompare(b.label)),
      };
    }
    case "gross": {
      const byGrade = new Map();
      for (const inv of invoicesInRange) {
        const gl    = glOf(inv);
        const fsAmt = feeOf(inv) ?? Number(inv.amount || 0);
        const invAmt = Number(inv.amount || 0);
        const id    = gl?.id ?? "unk";
        const lbl   = gl?.name ?? "Unclassified";
        if (!byGrade.has(id)) byGrade.set(id, { label: lbl, count: 0, gross: 0, grand: 0, students: [] });
        const r = byGrade.get(id); r.count++; r.gross += invAmt; r.grand += fsAmt;
        const stu = stuMap.get(String(inv.studentId));
        r.students.push({
          id: inv.studentId ?? `inv-${inv.id}`,
          name: stuName(stu),
          studentNumber: stu?.studentNumber || "",
          gross: invAmt,
          discount: fsAmt - invAmt,
        });
      }
      return {
        type: "gross",
        rows: [...byGrade.values()]
          .map(r => ({
            ...r,
            discount: r.grand - r.gross,
            students: r.students.sort((a,b) => a.name.localeCompare(b.name)),
          }))
          .sort((a,b) => a.label.localeCompare(b.label)),
      };
    }
    case "pay": {
      const effDate = p => p.paymentReceivedOn
        ? new Date(p.paymentReceivedOn).getTime()
        : new Date(p.paidAt || 0).getTime();
      const effLabel = p => {
        if (p.paymentReceivedOn) return fmtDate(p.paymentReceivedOn);
        return p.paidAt ? fmtDate(p.paidAt) : "—";
      };
      const rows = [...payments]
        .sort((a,b) => effDate(b) - effDate(a))
        .map(p => {
          const inv = invMap.get(String(p.invoiceId));
          const stu = inv ? stuMap.get(String(inv.studentId)) : null;
          return { date: effLabel(p), dateTs: effDate(p), student: stuName(stu), amount: Number(p.amount||0), method: p.method||"—" };
        });
      return { type: "pay", rows: rows.slice(0,100), allRows: rows, total: rows.length };
    }
    case "exp": {
      // Every manually-recorded, non-voided expense as its own line —
      // mirrors AdminDashboard.jsx's version of this breakdown. Payroll
      // is intentionally absent: it's shown separately (Money Flow
      // panel's "Configured payroll" tile) and no longer auto-blended
      // into Total Expenses — the owner controls this total entirely
      // through what they log on the Expenses page.
      const rows = expenses
        .filter(x => x.status !== "VOIDED")
        .map(x => ({
          date: fmtDate(x.expenseDate),
          dateTs: x.expenseDate ? new Date(x.expenseDate).getTime() : 0,
          category: x.category || "Miscellaneous",
          description: x.description || "—",
          payee: x.payee || "—",
          amount: Number(x.amount || 0),
          // Carried through (not shown as table columns) for the row-click
          // detail popup — full Notes text in particular doesn't fit inline.
          notes: x.notes || "",
          paymentMethod: x.paymentMethod || "",
          referenceNo: x.referenceNo || "",
          addedByUsername: x.addedByUsername || "",
          addedAt: x.addedAt || null,
        }))
        .sort((a,b) => b.dateTs - a.dateTs);
      return { type: "exp", rows };
    }
    case "dep": case "dep_neg": {
      return { type: "dep",
        totalPayments:  kpis?.totalPayments  ?? 0,
        totalExpenses:  kpis?.totalExpenses  ?? 0,
        currentDeposits: kpis?.currentDeposits ?? 0 };
    }
    case "col": {
      const paidByInv = new Map();
      for (const p of allPayments) paidByInv.set(String(p.invoiceId), (paidByInv.get(String(p.invoiceId))||0) + Number(p.amount||0));
      const byGrade = new Map();
      for (const inv of invoices) {
        const invAmt    = Number(inv.amount||0);
        const paid      = paidByInv.get(String(inv.id))||0;
        const remaining = Math.max(0, invAmt - paid);
        if (remaining < 0.005) continue;
        const gl  = glOf(inv);
        const id  = gl?.id ?? "unk";
        const lbl = gl?.name ?? "Unclassified";
        if (!byGrade.has(id)) byGrade.set(id, { label: lbl, count: 0, gross: 0, paid: 0, remaining: 0, students: [] });
        const r = byGrade.get(id); r.count++; r.gross += invAmt; r.paid += paid; r.remaining += remaining;
        const stu = stuMap.get(String(inv.studentId));
        r.students.push({
          id: inv.studentId ?? `inv-${inv.id}`,
          name: stuName(stu),
          studentNumber: stu?.studentNumber || "",
          gross: invAmt,
          paid,
          remaining,
        });
      }
      return {
        type: "col",
        rows: [...byGrade.values()]
          .map(r => ({ ...r, students: r.students.sort((a,b) => b.remaining - a.remaining) }))
          .sort((a,b) => b.remaining - a.remaining),
      };
    }
    default: return null;
  }
}

/** Small rotating chevron marking a grade-level row as expandable. Pure
 *  CSS rotation, no animation library — respects prefers-reduced-motion
 *  automatically since it's a static transform, not a running animation. */
function ExpandChevron({ open }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
         style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
         aria-hidden="true">
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

/** Nested per-student rows shown when a grade-level row in the Grand
 *  Total / Gross Total / Remaining Collectibles breakdown is expanded.
 *  `colSpan` must match the parent table's column count so the wrapping
 *  <td> spans correctly; `renderRow` produces each student's own <tr>. */
function StudentSubRows({ students, colSpan, renderRow }) {
  if (!students || students.length === 0) {
    return (
      <tr>
        <td className={s.fovModalSubCell} colSpan={colSpan}>
          <span className={s.fovModalSubEmpty}>No students in this group.</span>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td colSpan={colSpan} className={s.fovModalSubCell}>
        <table className={s.fovModalSubTable}>
          <tbody>{students.map((stu, i) => renderRow(stu, i))}</tbody>
        </table>
      </td>
    </tr>
  );
}

/** Default { key, dir } a breakdown table opens sorted by — mirrors the
 *  order buildFovDetail already produces so the first render looks
 *  unchanged; clicking a column header re-sorts from there. */
function defaultSortFor(type) {
  switch (type) {
    case "grand":
    case "gross":  return { key: "label", dir: "asc" };
    case "col":    return { key: "remaining", dir: "desc" };
    case "pay":    return { key: "dateTs", dir: "desc" };
    case "exp":    return { key: "dateTs", dir: "desc" };
    default:       return { key: null, dir: "asc" };
  }
}

function sortRows(rows, sort) {
  if (!sort?.key) return rows;
  const { key, dir } = sort;
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = a[key], bv = b[key];
    let cmp;
    if (typeof av === "string" || typeof bv === "string") {
      cmp = String(av ?? "").localeCompare(String(bv ?? ""), undefined, { sensitivity: "base" });
    } else {
      cmp = (Number(av) || 0) - (Number(bv) || 0);
    }
    return dir === "asc" ? cmp : -cmp;
  });
  return copy;
}

/** Clickable <th> that toggles sort on a breakdown table. Clicking a new
 *  column starts at `defaultDir`; clicking the active column flips it. */
function SortTh({ children, sortKey, sort, onSort, defaultDir = "asc", right }) {
  const active = sort.key === sortKey;
  return (
    <th
      className={`${s.fovModalTh} ${right ? s.fovModalThR : ""} ${s.fovModalThSort} ${active ? s.fovModalThSortActive : ""}`}
      onClick={() => onSort(sortKey, defaultDir)}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <span className={s.fovModalThInner}>
        {children}
        <svg className={s.fovModalSortIcon} width="9" height="9" viewBox="0 0 9 9" fill="currentColor" aria-hidden="true">
          {active && sort.dir === "asc"
            ? <path d="M4.5 1.5L8 6.5H1L4.5 1.5Z"/>
            : <path d="M4.5 7.5L1 2.5H8L4.5 7.5Z"/>}
        </svg>
      </span>
    </th>
  );
}

function fmtDateTimeShort(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleString("en-PH", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** Read-only detail popup for one expense row in the "Total Expenses"
 *  drill-down — surfaces the full Description and Notes text, neither of
 *  which fit in the table row. Stacks above FovInfoModal via its own
 *  createPortal + higher z-index. Mirrors AdminDashboard.jsx's version
 *  of the same popup. */
function ExpenseDetailPopup({ expense, onClose }) {
  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  return createPortal(
    <div className={s.fovDetailOverlay} role="dialog" aria-modal="true" aria-label="Expense details">
      <div className={s.fovDetailModal} onClick={e => e.stopPropagation()}>
        <div className={s.fovDetailHead}>
          <h3 className={s.fovDetailTitle}>Expense Details</h3>
          <button className={s.fovModalClose} onClick={onClose} aria-label="Close">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="1" y1="1" x2="12" y2="12"/><line x1="12" y1="1" x2="1" y2="12"/>
            </svg>
          </button>
        </div>
        <div className={s.fovDetailBody}>
          <div className={s.fovDetailTop}>
            <span className={s.fovModalBadge}>{expense.category}</span>
            <span className={s.fovDetailAmount}>{money(expense.amount)}</span>
          </div>
          <div className={s.fovDetailSection}>
            <div className={s.fovDetailLabel}>Description</div>
            <div className={s.fovDetailValue}>{expense.description || "—"}</div>
          </div>
          <div className={s.fovDetailGrid}>
            <div>
              <div className={s.fovDetailLabel}>Date</div>
              <div className={s.fovDetailValue}>{expense.date}</div>
            </div>
            <div>
              <div className={s.fovDetailLabel}>Payment method</div>
              <div className={s.fovDetailValue}>{expense.paymentMethod || "—"}</div>
            </div>
            <div>
              <div className={s.fovDetailLabel}>Paid to</div>
              <div className={s.fovDetailValue}>{expense.payee}</div>
            </div>
            <div>
              <div className={s.fovDetailLabel}>Reference #</div>
              <div className={s.fovDetailValue}>{expense.referenceNo || "—"}</div>
            </div>
          </div>
          <div className={s.fovDetailSection}>
            <div className={s.fovDetailLabel}>Notes</div>
            <div className={`${s.fovDetailValue} ${!expense.notes ? s.fovDetailValueMuted : ""}`}>
              {expense.notes || "No notes added."}
            </div>
          </div>
          {expense.addedByUsername && (
            <div className={s.fovDetailAudit}>
              Recorded by {expense.addedByUsername}
              {fmtDateTimeShort(expense.addedAt) ? ` · ${fmtDateTimeShort(expense.addedAt)}` : ""}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function FovInfoModal({ tile, onClose, rawData, kpis, scopeLabel }) {
  const infoKey = tile.key.replace("_neg","");
  const detail  = buildFovDetail(tile.key, rawData, kpis);

  const [sort, setSort] = useState(() => defaultSortFor(detail?.type));
  // Selected row for the "exp" table's click-through detail popup — the
  // only type with columns that don't fit inline (Notes has nowhere to go).
  const [expDetail, setExpDetail] = useState(null);
  // Which grade-level row (by label) is expanded to show its per-student
  // breakdown, for the "grand" / "gross" / "col" tables. One at a time
  // (accordion) keeps the modal from growing unpredictably tall.
  const [expandedRow, setExpandedRow] = useState(null);
  useEffect(() => {
    setSort(defaultSortFor(detail?.type));
    setExpDetail(null);
    setExpandedRow(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tile.key]);
  const onSort = (key, defaultDir) => setSort(prev =>
    prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: defaultDir });

  const sortedRows = useMemo(() => sortRows(detail?.rows || [], sort), [detail, sort]);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  const peso = v => `₱${Number(v||0).toLocaleString("en-PH",{minimumFractionDigits:2,maximumFractionDigits:2})}`;

  function DetailContent() {
    if (!detail) return null;
    if (detail.type === "grand") return (
      <>
        <div className={s.fovModalSectionLabel}>Breakdown by Grade Level</div>
        <div className={s.fovModalTableWrap}>
          <table className={s.fovModalTable}>
            <thead><tr>
              <SortTh sortKey="label" sort={sort} onSort={onSort} defaultDir="asc">Grade Level</SortTh>
              <SortTh sortKey="feeAmt" sort={sort} onSort={onSort} defaultDir="desc" right>Fee Structure</SortTh>
              <SortTh sortKey="count" sort={sort} onSort={onSort} defaultDir="desc" right>Students</SortTh>
              <SortTh sortKey="subtotal" sort={sort} onSort={onSort} defaultDir="desc" right>Subtotal</SortTh>
            </tr></thead>
            <tbody>
              {sortedRows.map((r,i) => {
                const open = expandedRow === r.label;
                return (
                  <Fragment key={i}>
                    <tr className={`${s.fovModalTr} ${s.fovModalTrClickable}`}
                        onClick={() => setExpandedRow(open ? null : r.label)}>
                      <td className={s.fovModalTd}>
                        <span className={s.fovModalExpandCell}>
                          <ExpandChevron open={open} />
                          {r.label}
                        </span>
                      </td>
                      <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{peso(r.feeAmt)}</td>
                      <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{r.count}</td>
                      <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(r.subtotal)}</td>
                    </tr>
                    {open && (
                      <StudentSubRows
                        colSpan={4}
                        students={r.students}
                        renderRow={(stu, si) => (
                          <tr key={si} className={s.fovModalSubTr}>
                            <td className={s.fovModalSubTd}>
                              {stu.name}{stu.studentNumber ? ` · ${stu.studentNumber}` : ""}
                            </td>
                            <td className={`${s.fovModalSubTd} ${s.fovModalTdR}`} colSpan={2}></td>
                            <td className={`${s.fovModalSubTd} ${s.fovModalTdR}`}>{peso(stu.amount)}</td>
                          </tr>
                        )}
                      />
                    )}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot><tr className={s.fovModalTrTotal}>
              <td className={s.fovModalTd} colSpan={2}>TOTAL</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{detail.rows.reduce((s,r)=>s+r.count,0)}</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(kpis?.grandTotal??0)}</td>
            </tr></tfoot>
          </table>
        </div>
      </>
    );
    if (detail.type === "gross") return (
      <>
        <div className={s.fovModalSectionLabel}>Breakdown by Grade Level</div>
        <div className={s.fovModalTableWrap}>
          <table className={s.fovModalTable}>
            <thead><tr>
              <SortTh sortKey="label" sort={sort} onSort={onSort} defaultDir="asc">Grade Level</SortTh>
              <SortTh sortKey="count" sort={sort} onSort={onSort} defaultDir="desc" right>Students</SortTh>
              <SortTh sortKey="gross" sort={sort} onSort={onSort} defaultDir="desc" right>Gross Total</SortTh>
              <SortTh sortKey="discount" sort={sort} onSort={onSort} defaultDir="desc" right>Discounts</SortTh>
            </tr></thead>
            <tbody>
              {sortedRows.map((r,i) => {
                const open = expandedRow === r.label;
                return (
                  <Fragment key={i}>
                    <tr className={`${s.fovModalTr} ${s.fovModalTrClickable}`}
                        onClick={() => setExpandedRow(open ? null : r.label)}>
                      <td className={s.fovModalTd}>
                        <span className={s.fovModalExpandCell}>
                          <ExpandChevron open={open} />
                          {r.label}
                        </span>
                      </td>
                      <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{r.count}</td>
                      <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(r.gross)}</td>
                      <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdDiscount}`}>{r.discount > 0.005 ? `−${peso(r.discount)}` : "—"}</td>
                    </tr>
                    {open && (
                      <StudentSubRows
                        colSpan={4}
                        students={r.students}
                        renderRow={(stu, si) => (
                          <tr key={si} className={s.fovModalSubTr}>
                            <td className={s.fovModalSubTd}>
                              {stu.name}{stu.studentNumber ? ` · ${stu.studentNumber}` : ""}
                            </td>
                            <td className={s.fovModalSubTd}></td>
                            <td className={`${s.fovModalSubTd} ${s.fovModalTdR}`}>{peso(stu.gross)}</td>
                            <td className={`${s.fovModalSubTd} ${s.fovModalTdR} ${s.fovModalTdDiscount}`}>{stu.discount > 0.005 ? `−${peso(stu.discount)}` : "—"}</td>
                          </tr>
                        )}
                      />
                    )}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot><tr className={s.fovModalTrTotal}>
              <td className={s.fovModalTd}>TOTAL</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{detail.rows.reduce((s,r)=>s+r.count,0)}</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(kpis?.grossTotal??0)}</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdDiscount}`}>{kpis && kpis.grandTotal > kpis.grossTotal ? `−${peso(kpis.grandTotal - kpis.grossTotal)}` : "—"}</td>
            </tr></tfoot>
          </table>
        </div>
      </>
    );
    if (detail.type === "pay") return (
      <>
        <div className={s.fovModalSectionLabel}>
          Payment Records {detail.total > 100 ? `(showing 100 of ${detail.total})` : `(${detail.total} total)`}
        </div>
        <div className={s.fovModalTableWrap}>
          <table className={s.fovModalTable}>
            <thead><tr>
              <SortTh sortKey="dateTs" sort={sort} onSort={onSort} defaultDir="desc">Date</SortTh>
              <SortTh sortKey="student" sort={sort} onSort={onSort} defaultDir="asc">Student</SortTh>
              <SortTh sortKey="method" sort={sort} onSort={onSort} defaultDir="asc" right>Method</SortTh>
              <SortTh sortKey="amount" sort={sort} onSort={onSort} defaultDir="desc" right>Amount</SortTh>
            </tr></thead>
            <tbody>
              {sortedRows.map((r,i) => (
                <tr key={i} className={s.fovModalTr}>
                  <td className={`${s.fovModalTd} ${s.fovModalTdDate}`}>{r.date}</td>
                  <td className={s.fovModalTd}>{r.student}</td>
                  <td className={`${s.fovModalTd} ${s.fovModalTdR}`}><span className={s.fovModalBadge}>{r.method}</span></td>
                  <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(r.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className={s.fovModalTrTotal}>
              <td className={s.fovModalTd} colSpan={3}>TOTAL PAYMENTS</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(kpis?.totalPayments??0)}</td>
            </tr></tfoot>
          </table>
        </div>
      </>
    );
    if (detail.type === "exp") return (
      <>
        <div className={s.fovModalSectionLabel}>Recorded Expenses ({detail.rows.length})</div>
        <div className={s.fovModalTableWrap}>
          <table className={s.fovModalTable}>
            <thead><tr>
              <SortTh sortKey="dateTs" sort={sort} onSort={onSort} defaultDir="desc">Date</SortTh>
              <SortTh sortKey="category" sort={sort} onSort={onSort} defaultDir="asc">Category</SortTh>
              <SortTh sortKey="description" sort={sort} onSort={onSort} defaultDir="asc">Description</SortTh>
              <SortTh sortKey="payee" sort={sort} onSort={onSort} defaultDir="asc">Paid To</SortTh>
              <SortTh sortKey="amount" sort={sort} onSort={onSort} defaultDir="desc" right>Amount</SortTh>
            </tr></thead>
            <tbody>
              {sortedRows.map((r,i) => (
                <tr
                  key={i}
                  className={`${s.fovModalTr} ${s.fovModalTrClickable}`}
                  onClick={() => setExpDetail(r)}
                  onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpDetail(r); } }}
                  tabIndex={0}
                  role="button"
                  title="Click for full details"
                >
                  <td className={`${s.fovModalTd} ${s.fovModalTdDate}`}>{r.date}</td>
                  <td className={s.fovModalTd}><span className={s.fovModalBadge}>{r.category}</span></td>
                  <td className={s.fovModalTd}>{r.description}</td>
                  <td className={s.fovModalTd}>{r.payee}</td>
                  <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(r.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className={s.fovModalTrTotal}>
              <td className={s.fovModalTd} colSpan={4}>TOTAL EXPENSES</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(kpis?.totalExpenses??0)}</td>
            </tr></tfoot>
          </table>
        </div>
      </>
    );
    if (detail.type === "dep") {
      const neg = detail.currentDeposits < -0.005;
      return (
        <>
          <div className={s.fovModalSectionLabel}>Calculation Breakdown</div>
          <div className={s.fovModalDepWrap}>
            <div className={`${s.fovModalDepRow} ${s.fovModalDepPlus}`}>
              <span className={s.fovModalDepLabel}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
                </svg>
                Total Payments Collected
              </span>
              <span className={s.fovModalDepValue}>{peso(detail.totalPayments)}</span>
            </div>
            <div className={`${s.fovModalDepRow} ${s.fovModalDepMinus}`}>
              <span className={s.fovModalDepLabel}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                Total Monthly Expenses
              </span>
              <span className={s.fovModalDepValue}>−{peso(detail.totalExpenses)}</span>
            </div>
            <div className={`${s.fovModalDepRow} ${s.fovModalDepTotal} ${neg ? s.fovModalDepNeg : s.fovModalDepPos}`}>
              <span className={s.fovModalDepLabel}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
                {neg ? "Net Deficit" : "Net Surplus"}
              </span>
              <span className={s.fovModalDepValue}>{peso(detail.currentDeposits)}</span>
            </div>
          </div>
        </>
      );
    }
    if (detail.type === "col") return (
      <>
        <div className={s.fovModalSectionLabel}>Outstanding by Grade Level</div>
        <div className={s.fovModalTableWrap}>
          <table className={s.fovModalTable}>
            <thead><tr>
              <SortTh sortKey="label" sort={sort} onSort={onSort} defaultDir="asc">Grade Level</SortTh>
              <SortTh sortKey="count" sort={sort} onSort={onSort} defaultDir="desc" right>Students</SortTh>
              <SortTh sortKey="gross" sort={sort} onSort={onSort} defaultDir="desc" right>Billed</SortTh>
              <SortTh sortKey="paid" sort={sort} onSort={onSort} defaultDir="desc" right>Paid</SortTh>
              <SortTh sortKey="remaining" sort={sort} onSort={onSort} defaultDir="desc" right>Remaining</SortTh>
            </tr></thead>
            <tbody>
              {sortedRows.map((r,i) => {
                const open = expandedRow === r.label;
                return (
                  <Fragment key={i}>
                    <tr className={`${s.fovModalTr} ${s.fovModalTrClickable}`}
                        onClick={() => setExpandedRow(open ? null : r.label)}>
                      <td className={s.fovModalTd}>
                        <span className={s.fovModalExpandCell}>
                          <ExpandChevron open={open} />
                          {r.label}
                        </span>
                      </td>
                      <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{r.count}</td>
                      <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{peso(r.gross)}</td>
                      <td className={`${s.fovModalTd} ${s.fovModalTdR}`}>{peso(r.paid)}</td>
                      <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(r.remaining)}</td>
                    </tr>
                    {open && (
                      <StudentSubRows
                        colSpan={5}
                        students={r.students}
                        renderRow={(stu, si) => (
                          <tr key={si} className={s.fovModalSubTr}>
                            <td className={s.fovModalSubTd}>
                              {stu.name}{stu.studentNumber ? ` · ${stu.studentNumber}` : ""}
                            </td>
                            <td className={s.fovModalSubTd}></td>
                            <td className={`${s.fovModalSubTd} ${s.fovModalTdR}`}>{peso(stu.gross)}</td>
                            <td className={`${s.fovModalSubTd} ${s.fovModalTdR}`}>{peso(stu.paid)}</td>
                            <td className={`${s.fovModalSubTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(stu.remaining)}</td>
                          </tr>
                        )}
                      />
                    )}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot><tr className={s.fovModalTrTotal}>
              <td className={s.fovModalTd} colSpan={4}>TOTAL REMAINING</td>
              <td className={`${s.fovModalTd} ${s.fovModalTdR} ${s.fovModalTdAccent}`}>{peso(kpis?.remainingCollectibles??0)}</td>
            </tr></tfoot>
          </table>
        </div>
      </>
    );
    return null;
  }

  return (
    <>
      {createPortal(
        <div className={s.fovOverlay} role="dialog" aria-modal="true" aria-label={`${tile.label} details`}>
          <div className={s.fovModal} onClick={e => e.stopPropagation()}>
            <div className={s.fovModalHead}>
              <div className={`${s.fovModalHeadIcon} ${s[`fovModalIcon_${infoKey}`]}`}>{tile.icon}</div>
              <div className={s.fovModalHeadText}>
                <div className={s.fovModalEyebrow}>Financial Metric Detail</div>
                <div className={s.fovModalTitle}>{tile.label}</div>
              </div>
              <button
                type="button"
                className={s.fovModalPdfBtn}
                onClick={() => exportFinanceDetailPdf({
                  title: tile.label,
                  detail,
                  // Payments: every record, not just the 100 on screen.
                  rows: sortRows(detail?.allRows || detail?.rows || [], sort),
                  kpis,
                  scopeLabel,
                })}
                disabled={!detail}
                title="Download this breakdown as a PDF">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                PDF
              </button>
              <button className={s.fovModalClose} onClick={onClose} aria-label="Close">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="1" y1="1" x2="12" y2="12"/><line x1="12" y1="1" x2="1" y2="12"/>
                </svg>
              </button>
            </div>
            <div className={s.fovModalBody}>
              <DetailContent />
            </div>
          </div>
        </div>,
        document.body
      )}
      {expDetail && <ExpenseDetailPopup expense={expDetail} onClose={() => setExpDetail(null)} />}
    </>
  );
}

/** Money flow — tuition + book purchases on the IN side, staff salaries on
 *  the OUT side. Salaries aren't recorded as individual payments yet, so we
 *  treat the Employee.salary column as the monthly expected outflow and
 *  annotate it clearly. That's honest — an actual payroll ledger would be a
 *  future feature. */
export default function FinanceTab() {
  const [payments,    setPayments]    = useState([]);
  const [purchases,   setPurchases]   = useState([]);
  const [employees,   setEmployees]   = useState([]);
  const [students,    setStudents]    = useState([]);
  const [books,       setBooks]       = useState([]);
  const [invoices,    setInvoices]    = useState([]);
  const [sections,      setSections]      = useState([]);
  const [gradeLevels,   setGradeLevels]   = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [expenses,      setExpenses]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [err,         setErr]         = useState("");
  const [range,       setRange]       = useState("30");
  // Custom-range pickers — only consulted when `range === "custom"`.
  // Default "to" to today, "from" to ~30 days ago so the picker opens
  // on a sensible window when the user first switches to Custom mode.
  const [customFrom, setCustomFrom] = useState(() => isoToday(-30));
  const [customTo,   setCustomTo]   = useState(() => isoToday(0));
  const [dateField,  setDateField]  = useState("received");
  const [subTab,      setSubTab]      = useState("tuition");
  const payrollOn = usePayrollEnabled();
  // If the admin disables Payroll while the report is open on the
  // Payroll sub-tab, snap back to Tuition so they aren't stuck on a
  // tab that's about to disappear.
  useEffect(() => {
    if (!payrollOn && subTab === "payroll") setSubTab("tuition");
  }, [payrollOn, subTab]);
  // Payment details modal — opens when the admin clicks the info icon on a
  // row. Carries everything we've already resolved for that payment so the
  // modal doesn't need to re-query.
  const [infoPayment, setInfoPayment] = useState(null);
  // Per-student tally panel — search + grade filter + sort key + the
  // currently-open student (info icon → modal showing every payment +
  // book purchase for that student in the active window).
  const [studentSearch, setStudentSearch]   = useState("");
  const [studentGrade,  setStudentGrade]    = useState("all");
  const [studentSort,   setStudentSort]     = useState({ key: "total", dir: "desc" });
  const [infoStudent,   setInfoStudent]     = useState(null);
  // Sort keys for the Recent payments / purchases lists at the bottom of
  // the tab. "date" is the default (newest first) so the lists keep their
  // feel; toggling cycles asc/desc on the same key.
  const [paymentSort,  setPaymentSort]  = useState({ key: "date", dir: "desc" });
  const [purchaseSort, setPurchaseSort] = useState({ key: "date", dir: "desc" });

  // Payment-ledger filters — per-payment audit view (one row per payment,
  // not per student). Built for OR# auditing: search any field, filter by
  // method, and a one-click "missing OR only" toggle for the reconciliation
  // case where the cashier needs to find payments that slipped through
  // without an Official Receipt number.
  const [ledgerSearch,     setLedgerSearch]     = useState("");
  const [ledgerMethod,     setLedgerMethod]     = useState("all");
  const [ledgerMissingOR,  setLedgerMissingOR]  = useState(false);
  const [ledgerSort,       setLedgerSort]       = useState({ key: "date", dir: "desc" });
  const [fovOpenInfo,      setFovOpenInfo]      = useState(null);
  const [tileInfo,         setTileInfo]         = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr("");
    Promise.all([
      fetch(`${API_BASE}/api/tuition-payments`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/student-book-purchases`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/employees?all=true`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/students`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/books`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/tuition-invoices`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/sections`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/fee-structures`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/expenses`).then(r => r.ok ? r.json() : []),
    ]).then(([pay, pur, emp, stu, bk, inv, sec, grd, feeStr, exp]) => {
      if (cancelled) return;
      setPayments(Array.isArray(pay) ? pay : []);
      setPurchases(Array.isArray(pur) ? pur : []);
      setEmployees(Array.isArray(emp) ? emp : []);
      setStudents(Array.isArray(stu) ? stu : []);
      setBooks(Array.isArray(bk) ? bk : []);
      setInvoices(Array.isArray(inv) ? inv : []);
      setSections(Array.isArray(sec) ? sec : []);
      setGradeLevels(Array.isArray(grd) ? grd : []);
      setFeeStructures(Array.isArray(feeStr) ? feeStr : []);
      setExpenses(Array.isArray(exp) ? exp : []);
    }).catch(e => !cancelled && setErr(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const studentById  = useMemo(() => Object.fromEntries(students.map(x => [x.id, x])), [students]);
  const bookById     = useMemo(() => Object.fromEntries(books.map(x => [x.id, x])),    [books]);
  const invoiceById  = useMemo(() => Object.fromEntries(invoices.map(x => [x.id, x])), [invoices]);
  const sectionById  = useMemo(() => Object.fromEntries(sections.map(x => [x.id, x])), [sections]);
  const gradeById    = useMemo(() => Object.fromEntries(gradeLevels.map(x => [x.id, x])), [gradeLevels]);
  const employeeById = useMemo(() => Object.fromEntries(employees.map(x => [x.id, x])), [employees]);

  /** Resolve a payment's student / section / grade / processor from the
   *  loaded lookups. Returns a shape with everything null-safe so the row
   *  and modal can render without littering the JSX with optional chains. */
  function paymentContext(p) {
    const invoice   = invoiceById[p.invoiceId] || null;
    const student   = invoice ? studentById[invoice.studentId] : null;
    const section   = student?.currentSectionId ? sectionById[student.currentSectionId] : null;
    const grade     = section?.gradeLevelId ? gradeById[section.gradeLevelId] : null;
    const processor = p.receivedByStaffId ? employeeById[p.receivedByStaffId] : null;
    return { invoice, student, section, grade, processor };
  }

  function openTuitionTile() {
    const rows = paymentsInRange.map(p => ({ ...p, ...paymentContext(p) }));
    setTileInfo({ type: "tuition", rows, total: tuitionIn });
  }

  function openBooksTile() {
    const rows = purchasesInRange.map(p => {
      const student = p.studentId ? studentById[p.studentId] : null;
      const section = student?.currentSectionId ? sectionById[student.currentSectionId] : null;
      const grade   = section?.gradeLevelId ? gradeById[section.gradeLevelId] : null;
      const book    = bookById[p.bookId] || null;
      return { ...p, student, section, grade, book };
    });
    setTileInfo({ type: "books", rows, total: booksIn });
  }

  function openTotalTile() {
    const tuitionRows = paymentsInRange.map(p => ({
      ...p, ...paymentContext(p),
      _kind: "Tuition", _date: p.paymentReceivedOn || p.paidAt,
    }));
    const bookRows = purchasesInRange.map(p => {
      const student = p.studentId ? studentById[p.studentId] : null;
      const section = student?.currentSectionId ? sectionById[student.currentSectionId] : null;
      const grade   = section?.gradeLevelId ? gradeById[section.gradeLevelId] : null;
      return { ...p, student, section, grade, book: bookById[p.bookId] || null,
               _kind: "Book", _date: p.purchasedAt };
    });
    const rows = [...tuitionRows, ...bookRows]
      .sort((a, b) => new Date(b._date || 0) - new Date(a._date || 0));
    setTileInfo({ type: "total", rows, total: totalIn });
  }

  function openPayrollTile() {
    const rows = payrollRows.slice().sort((a, b) => Number(b.salary || 0) - Number(a.salary || 0));
    setTileInfo({ type: "payroll", rows, total: monthlyPayroll });
  }

  /** Filter tuition payments / book purchases to the active time window.
   *  Both bounds are honoured: `from` is the inclusive lower bound,
   *  `to` is the exclusive upper. For "all", from=0 and to=Infinity
   *  short-circuit any meaningful comparison. */
  const { from: rangeFrom, to: rangeTo } = computeBounds(range, customFrom, customTo);

  const paymentsInRange = useMemo(() => payments.filter(p => {
    const t = paymentTsByField(p, dateField);
    if (Number.isNaN(t)) return false;
    return t >= rangeFrom && t < rangeTo;
  }), [payments, rangeFrom, rangeTo, dateField]);

  const purchasesInRange = useMemo(() => purchases.filter(p => {
    const t = new Date(p.purchasedAt).getTime();
    if (Number.isNaN(t)) return false;
    return t >= rangeFrom && t < rangeTo;
  }), [purchases, rangeFrom, rangeTo]);

  /** Invoices actually BILLED within the window — by invoice.createdAt.
   *  Feeds Grand Total / Gross Total when a window narrower than "All
   *  time" is selected. Filter Date By (received/entered) intentionally
   *  doesn't apply here: an invoice has one creation date, not a
   *  separate "received" concept the way a payment does. Remaining
   *  Collectibles deliberately does NOT use this — it's a live balance,
   *  not a period total, so it always sums every invoice regardless of
   *  the window (see fovMetrics below). */
  const invoicesInRange = useMemo(() => invoices.filter(inv => {
    const t = inv.createdAt ? new Date(inv.createdAt).getTime() : NaN;
    if (Number.isNaN(t)) return false;
    return t >= rangeFrom && t < rangeTo;
  }), [invoices, rangeFrom, rangeTo]);

  const tuitionIn = paymentsInRange.reduce((s, p) => s + Number(p.amount || 0), 0);
  const booksIn   = purchasesInRange.reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalIn   = tuitionIn + booksIn;

  /** Breakdown of tuition payments by method (GCASH / CASH / …). Missing or
   *  blank methods are bucketed as "UNSPECIFIED" so no peso gets lost.
   *  Sorted by amount descending so the biggest channel shows first. */
  /** Bucket key for the per-method breakdown. BANK payments split further by
   *  bankName so "BANK · BDO" and "BANK · BPI" show up as separate cards —
   *  accountants reconciling a specific bank statement need the per-bank
   *  total, not a lumped-together BANK row. A bank transfer with no bank
   *  name recorded falls into "BANK · (unspecified)" so legacy data is
   *  still visible. */
  function methodKey(p) {
    const method = (p.method || "").trim().toUpperCase() || "UNSPECIFIED";
    if (method !== "BANK") return method;
    const bank = (p.bankName || "").trim();
    return bank ? `BANK · ${bank}` : "BANK · (unspecified)";
  }

  const methodBreakdown = useMemo(() => {
    const totals = new Map();   // methodKey → { amount, count }
    for (const p of paymentsInRange) {
      const key = methodKey(p);
      const prev = totals.get(key) || { amount: 0, count: 0 };
      totals.set(key, { amount: prev.amount + Number(p.amount || 0), count: prev.count + 1 });
    }
    const list = Array.from(totals, ([method, v]) => ({
      method, amount: v.amount, count: v.count,
      share: tuitionIn > 0 ? v.amount / tuitionIn : 0,
    }));
    list.sort((a, b) => b.amount - a.amount);
    return list;
  }, [paymentsInRange, tuitionIn]);

  /** Per-method drill-down: which students paid via this method and how
   *  much. Keyed by method, value is a list of {studentId, name, amount,
   *  count} sorted by amount desc. Shared lookups mean this is cheap to
   *  recompute whenever the window changes. */
  const methodStudentBreakdown = useMemo(() => {
    const byMethod = new Map();   // methodKey → Map<studentKey, row>
    for (const p of paymentsInRange) {
      const method = methodKey(p);
      const invoice = invoiceById[p.invoiceId] || null;
      const student = invoice ? studentById[invoice.studentId] : null;
      const key = student?.id ?? `invoice-${p.invoiceId ?? "unknown"}`;
      // When the chain payment → invoice → student breaks, label the row
      // by what's actually wrong so an admin reading the report knows it's
      // a data-cleanup issue (not a missing-name display bug). Orphans
      // can be cleared from Settings → Maintenance → Orphan payments.
      let name;
      if (student) {
        name = `${student.firstName} ${student.lastName}`.trim();
      } else if (p.studentName) {
        name = p.studentName;
      } else if (!invoice) {
        name = `Orphan payment · invoice #${p.invoiceId ?? "—"} (deleted)`;
      } else {
        name = `Orphan payment · student #${invoice.studentId ?? "—"} (deleted)`;
      }
      const map = byMethod.get(method) || new Map();
      const prev = map.get(key) || { studentId: student?.id ?? null, name, amount: 0, count: 0 };
      prev.amount += Number(p.amount || 0);
      prev.count  += 1;
      map.set(key, prev);
      byMethod.set(method, map);
    }
    const out = {};
    for (const [method, map] of byMethod) {
      const list = Array.from(map.values()).sort((a, b) => b.amount - a.amount);
      out[method] = list;
    }
    return out;
  }, [paymentsInRange, invoiceById, studentById]);

  /** Active staff with a configured salary — shown at the bottom as a
   *  snapshot only. We DON'T multiply by the window, because no payroll
   *  payments have actually been recorded. Once there's a payroll ledger,
   *  an "actual money out" tile can join the ones above. */
  const payrollRows = useMemo(
    () => employees.filter(e => e.salary && (e.status || "Active") !== "Inactive"),
    [employees]
  );
  const monthlyPayroll = payrollRows.reduce((s, e) => s + Number(e.salary || 0), 0);

  /** Manually-recorded, non-voided expenses (utilities, rent, supplies,
   *  etc.) — the actual outflow ledger, distinct from the payroll
   *  snapshot above. Scoped to the same Window + Filter Date By as
   *  payments, so "Total Expenses" (and Current Deposits, which nets
   *  against it) actually move when the window changes — previously this
   *  summed every expense ever recorded regardless of the selected
   *  range, which silently contradicted this page's own "Payments,
   *  Expenses & Deposits reflect the selected window" subtitle. */
  const expensesInRange = useMemo(() => expenses.filter(e => {
    if (e.status === "VOIDED") return false;
    const t = expenseTsByField(e, dateField);
    if (Number.isNaN(t)) return false;
    return t >= rangeFrom && t < rangeTo;
  }), [expenses, rangeFrom, rangeTo, dateField]);

  const adHocExpenses = useMemo(
    () => expensesInRange.reduce((s, e) => s + Number(e.amount || 0), 0),
    [expensesInRange]
  );

  // ── Financial Overview — 6 headline metrics ────────────────────────────
  // Fee structure lookup: gradeLevelId|schoolYearId → undiscounted totalAmount
  const fovFeeMap = useMemo(() => {
    const m = new Map();
    for (const fs of feeStructures) {
      m.set(`${fs.gradeLevelId}|${fs.schoolYearId}`, Number(fs.totalAmount || 0));
    }
    return m;
  }, [feeStructures]);

  /** Sums a list of invoices into { grand, gross } — grand uses the fee
   *  structure's undiscounted totalAmount when resolvable, gross uses
   *  the invoice's actual (post-discount) amount. Shared by the
   *  windowed and all-time totals below so the two can never drift
   *  apart from using slightly different logic. */
  function sumInvoices(list) {
    let grand = 0, gross = 0;
    for (const inv of list) {
      const stu   = studentById[inv.studentId];
      const sec   = stu?.currentSectionId ? sectionById[stu.currentSectionId] : null;
      const fsAmt = sec?.gradeLevelId ? fovFeeMap.get(`${sec.gradeLevelId}|${inv.schoolYearId}`) : null;
      grand += fsAmt != null ? fsAmt : Number(inv.amount || 0);
      gross += Number(inv.amount || 0);
    }
    return { grand, gross };
  }

  const fovMetrics = useMemo(() => {
    // GRAND TOTAL & GROSS TOTAL — invoices billed WITHIN the selected
    // window (by invoice.createdAt), same mechanism as Payments/Expenses.
    const { grand: grandTotal, gross: grossTotal } = sumInvoices(invoicesInRange);
    // Remaining Collectibles is a live balance ("what's still owed right
    // now"), not a period total — it always needs the FULL invoice
    // universe and ALL payments ever, regardless of the selected window,
    // so it deliberately does not reuse grossTotal/allTimePaid-in-window.
    const allTimeGross = sumInvoices(invoices).gross;
    const allTimePaid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
    // Payments inside the selected window (tuition + books) for deposits.
    const totalPayments = tuitionIn + booksIn;
    // Deliberately NOT blended with the "Configured payroll" figure shown
    // elsewhere on this tab (Money Flow panel) — the owner controls Total
    // Expenses entirely through what they log on the Expenses page.
    const totalExpenses = adHocExpenses;
    const currentDeposits = totalPayments - totalExpenses;
    const remainingCollectibles = Math.max(0, allTimeGross - allTimePaid);
    return {
      grandTotal, grossTotal, totalPayments, totalExpenses,
      currentDeposits, remainingCollectibles,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, invoicesInRange, payments, studentById, sectionById, fovFeeMap, tuitionIn, booksIn, adHocExpenses]);

  /** Per-student tally — combine every tuition payment + book purchase in
   *  the active window into one row per student. Orphan payments (where
   *  the invoice → student chain is broken) bucket into a synthetic
   *  "Orphan · invoice #X" row so no peso disappears from the panel total.
   *  The shape carries the raw payments / purchases too so the info-icon
   *  modal can render them without re-walking the lists. */
  const studentTallies = useMemo(() => {
    const byKey = new Map();   // studentId | "orphan-INV" → row
    function ensure(key, seedName, seedStudent) {
      let row = byKey.get(key);
      if (!row) {
        const section = seedStudent?.currentSectionId ? sectionById[seedStudent.currentSectionId] : null;
        const grade   = section?.gradeLevelId ? gradeById[section.gradeLevelId] : null;
        row = {
          key,
          studentId: seedStudent?.id ?? null,
          student:   seedStudent || null,
          name:      seedName,
          grade,
          section,
          studentNumber: seedStudent?.studentNumber || "",
          tuitionAmount: 0,
          tuitionCount:  0,
          booksAmount:   0,
          booksCount:    0,
          payments:      [],
          purchases:     [],
        };
        byKey.set(key, row);
      }
      return row;
    }
    for (const p of paymentsInRange) {
      const invoice = invoiceById[p.invoiceId] || null;
      const student = invoice ? studentById[invoice.studentId] : null;
      const key = student?.id ?? `orphan-inv-${p.invoiceId ?? "unknown"}`;
      const name = student
        ? `${student.firstName || ""} ${student.lastName || ""}`.trim()
        : (p.studentName || `Orphan · invoice #${p.invoiceId ?? "—"}`);
      const row = ensure(key, name, student);
      row.tuitionAmount += Number(p.amount || 0);
      row.tuitionCount  += 1;
      row.payments.push(p);
    }
    for (const p of purchasesInRange) {
      const student = studentById[p.studentId] || null;
      const key = student?.id ?? `orphan-stu-${p.studentId ?? "unknown"}`;
      const name = student
        ? `${student.firstName || ""} ${student.lastName || ""}`.trim()
        : `Orphan · student #${p.studentId ?? "—"}`;
      const row = ensure(key, name, student);
      row.booksAmount += Number(p.amount || 0);
      row.booksCount  += 1;
      row.purchases.push(p);
    }
    const list = Array.from(byKey.values()).map(r => ({
      ...r,
      total: r.tuitionAmount + r.booksAmount,
      txCount: r.tuitionCount + r.booksCount,
    }));
    return list;
  }, [paymentsInRange, purchasesInRange, invoiceById, studentById, sectionById, gradeById]);

  // Reverse lookup: studentId → tally row. Lets us reuse the existing
  // StudentDetailsModal when the user clicks a student name anywhere on
  // the tab (per-method tables, per-student table, etc.) without
  // re-walking the tally list at click time.
  const tallyByStudentId = useMemo(() => {
    const map = new Map();
    for (const r of studentTallies) {
      if (r.studentId != null) map.set(r.studentId, r);
    }
    return map;
  }, [studentTallies]);

  // Apply the panel's search + grade filter, then sort by the active key.
  const visibleStudentTallies = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    let list = studentTallies.filter(r => {
      if (studentGrade !== "all" && String(r.grade?.id ?? "") !== String(studentGrade)) return false;
      if (q) {
        const hay = `${r.name} ${r.studentNumber} ${r.section?.name || ""} ${r.grade?.name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const dir = studentSort.dir === "asc" ? 1 : -1;
    list = list.slice().sort((a, b) => {
      const k = studentSort.key;
      if (k === "name") return a.name.localeCompare(b.name) * dir;
      return ((Number(a[k]) || 0) - (Number(b[k]) || 0)) * dir;
    });
    return list;
  }, [studentTallies, studentSearch, studentGrade, studentSort]);

  function toggleStudentSort(key) {
    setStudentSort(prev => prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: key === "name" ? "asc" : "desc" });
  }

  // Sorted variants of the in-range lists used by the Recent panels. We
  // resolve the student-name sort key via the same invoice → student
  // lookups already populated for the rest of the tab, so toggling sort
  // never triggers a new fetch and never widens the data the user can see.
  const sortedPaymentsInRange = useMemo(() => {
    const list = paymentsInRange.slice();
    const dir = paymentSort.dir === "asc" ? 1 : -1;
    const keyOf = (p) => {
      if (paymentSort.key === "date")   return effectivePaymentTs(p);
      if (paymentSort.key === "amount") return Number(p.amount || 0);
      if (paymentSort.key === "or")     return String(p.orNumber || "").toLowerCase();
      if (paymentSort.key === "name") {
        const inv = invoiceById[p.invoiceId] || null;
        const st  = inv ? studentById[inv.studentId] : null;
        return (st
          ? `${st.lastName || ""} ${st.firstName || ""}`.trim()
          : (p.studentName || "")
        ).toLowerCase();
      }
      return 0;
    };
    list.sort((a, b) => {
      const ka = keyOf(a), kb = keyOf(b);
      if (typeof ka === "string") return ka.localeCompare(kb) * dir;
      return (ka - kb) * dir;
    });
    return list;
  }, [paymentsInRange, paymentSort, invoiceById, studentById]);

  const sortedPurchasesInRange = useMemo(() => {
    const list = purchasesInRange.slice();
    const dir = purchaseSort.dir === "asc" ? 1 : -1;
    const keyOf = (p) => {
      if (purchaseSort.key === "date")   return new Date(p.purchasedAt || 0).getTime();
      if (purchaseSort.key === "amount") return Number(p.amount || 0);
      if (purchaseSort.key === "title") {
        const book = bookById[p.bookId];
        return (book?.title || "").toLowerCase();
      }
      if (purchaseSort.key === "name") {
        const st = studentById[p.studentId];
        return (st
          ? `${st.lastName || ""} ${st.firstName || ""}`.trim()
          : ""
        ).toLowerCase();
      }
      return 0;
    };
    list.sort((a, b) => {
      const ka = keyOf(a), kb = keyOf(b);
      if (typeof ka === "string") return ka.localeCompare(kb) * dir;
      return (ka - kb) * dir;
    });
    return list;
  }, [purchasesInRange, purchaseSort, bookById, studentById]);

  function togglePaymentSort(key) {
    setPaymentSort(prev => prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: (key === "name" || key === "or" || key === "title") ? "asc" : "desc" });
  }
  function togglePurchaseSort(key) {
    setPurchaseSort(prev => prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: (key === "name" || key === "title") ? "asc" : "desc" });
  }

  // ── Payment-ledger view ────────────────────────────────────────────────
  // Pre-decorate each in-range payment with its resolved student / section
  // context so the filter pass below stays readable. Done in one walk so
  // the search predicate doesn't repeatedly hit the lookups for every
  // payment × every keystroke.
  const ledgerRows = useMemo(() => {
    return paymentsInRange.map(p => {
      const ctx = paymentContext(p);
      const stName = ctx.student
        ? `${ctx.student.firstName || ""} ${ctx.student.lastName || ""}`.trim()
        : (p.studentName || "");
      const methodLabel = methodKey(p); // matches the per-method panel's key
      return { payment: p, ctx, stName, methodLabel };
    });
  }, [paymentsInRange, invoiceById, studentById, sectionById, gradeById, employeeById]);
  // ^ deps include the lookup maps so a fresh fetch repopulates names
  // without an explicit re-render trigger. paymentContext / methodKey
  // both close over these maps; listing them keeps useMemo honest.

  // Distinct method labels for the filter dropdown. Pulled from the live
  // ledger so a quiet month doesn't list methods that have no payments.
  const ledgerMethodOptions = useMemo(() => {
    const set = new Set();
    for (const r of ledgerRows) set.add(r.methodLabel);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [ledgerRows]);

  const visibleLedgerRows = useMemo(() => {
    const q = ledgerSearch.trim().toLowerCase();
    let list = ledgerRows.filter(r => {
      const p = r.payment;
      if (ledgerMethod !== "all" && r.methodLabel !== ledgerMethod) return false;
      if (ledgerMissingOR && (p.orNumber || "").trim() !== "") return false;
      if (q) {
        const hay = [
          r.stName,
          r.ctx.student?.studentNumber,
          r.ctx.grade?.name,
          r.ctx.section?.name,
          p.orNumber,
          p.referenceNo,
          p.method,
          p.bankName,
          p.particular,
          p.notes,
          r.ctx.processor?.name,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const dir = ledgerSort.dir === "asc" ? 1 : -1;
    const keyOf = (r) => {
      const p = r.payment;
      if (ledgerSort.key === "date")    return effectivePaymentTs(p);
      if (ledgerSort.key === "amount")  return Number(p.amount || 0);
      if (ledgerSort.key === "or")      return String(p.orNumber || "").toLowerCase();
      if (ledgerSort.key === "method")  return r.methodLabel.toLowerCase();
      if (ledgerSort.key === "name")    return r.stName.toLowerCase();
      if (ledgerSort.key === "ref")     return String(p.referenceNo || "").toLowerCase();
      return 0;
    };
    list = list.slice().sort((a, b) => {
      const ka = keyOf(a), kb = keyOf(b);
      if (typeof ka === "string") return ka.localeCompare(kb) * dir;
      return (ka - kb) * dir;
    });
    return list;
  }, [ledgerRows, ledgerSearch, ledgerMethod, ledgerMissingOR, ledgerSort]);

  const ledgerTotals = useMemo(() => ({
    count: visibleLedgerRows.length,
    amount: visibleLedgerRows.reduce((s, r) => s + Number(r.payment.amount || 0), 0),
    missing: visibleLedgerRows.filter(r => !(r.payment.orNumber || "").trim()).length,
  }), [visibleLedgerRows]);

  function toggleLedgerSort(key) {
    setLedgerSort(prev => prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: (key === "name" || key === "or" || key === "method" || key === "ref") ? "asc" : "desc" });
  }

  function exportLedgerCsv() {
    const rangeLabel = range === "custom"
      ? customRangeLabel(customFrom, customTo)
      : (RANGES.find(r => r.value === range)?.label || "current window");
    const header = ["Window", rangeLabel];
    const filterBits = [
      ledgerMethod !== "all"        ? `Method=${ledgerMethod}` : null,
      ledgerMissingOR              ? "Missing OR only"          : null,
      ledgerSearch.trim()           ? `Search="${ledgerSearch.trim()}"` : null,
    ].filter(Boolean);
    const filterRow = filterBits.length > 0 ? ["Filters", filterBits.join(" · ")] : null;
    const cols = ["Date Received", "Student", "Student #", "Grade", "Section",
                  "OR #", "Method", "Bank", "Reference #", "Amount",
                  "Particular", "Processed by", "Notes"];
    const rows = visibleLedgerRows.map(r => {
      const p = r.payment;
      const dateVal = p.paymentReceivedOn || (p.paidAt ? new Date(p.paidAt).toISOString() : "");
      return [
        dateVal, r.stName,
        r.ctx.student?.studentNumber || "",
        r.ctx.grade?.name || "",
        r.ctx.section?.name || "",
        p.orNumber || "",
        p.method || "",
        p.bankName || "",
        p.referenceNo || "",
        Number(p.amount || 0).toFixed(2),
        p.particular || "",
        r.ctx.processor?.name || "",
        p.notes || "",
      ];
    });
    const totalRow = ["TOTAL", `${ledgerTotals.count} payments`, "", "", "", "",
                      "", "", "", ledgerTotals.amount.toFixed(2), "", "", ""];
    const lines = [header];
    if (filterRow) lines.push(filterRow);
    lines.push([], cols, ...rows, [], totalRow);
    const csv = lines
      .map(r => r.map(v => {
        const sv = v == null ? "" : String(v);
        return /[",\n]/.test(sv) ? `"${sv.replace(/"/g, '""')}"` : sv;
      }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payments-ledger_${(rangeLabel || "all").replace(/\s+/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Total count of orphan-OR rows across the *unfiltered* ledger — drives
  // the chip count badge on the "Missing OR" toggle so the operator can
  // see at a glance whether there's anything to audit, even when the
  // current filter has them all hidden.
  const ledgerMissingORCountTotal = useMemo(
    () => ledgerRows.filter(r => !(r.payment.orNumber || "").trim()).length,
    [ledgerRows]
  );

  const studentTotals = useMemo(() => ({
    tuition: visibleStudentTallies.reduce((s, r) => s + r.tuitionAmount, 0),
    books:   visibleStudentTallies.reduce((s, r) => s + r.booksAmount,   0),
    total:   visibleStudentTallies.reduce((s, r) => s + r.total,         0),
    tx:      visibleStudentTallies.reduce((s, r) => s + r.txCount,       0),
  }), [visibleStudentTallies]);

  // Headline stats for the panel — computed off the filtered list so the
  // numbers move with the search / grade selectors.
  const studentTopSpender = useMemo(() => {
    if (!visibleStudentTallies.length) return null;
    return visibleStudentTallies.reduce((best, r) => r.total > (best?.total ?? -1) ? r : best, null);
  }, [visibleStudentTallies]);

  const studentAvgPerFamily = visibleStudentTallies.length
    ? studentTotals.total / visibleStudentTallies.length
    : 0;

  function exportStudentTalliesCsv() {
    const rangeLabel = range === "custom"
      ? customRangeLabel(customFrom, customTo)
      : (RANGES.find(r => r.value === range)?.label || "current window");
    const header = ["Window", rangeLabel];
    const cols = ["Student", "Student #", "Grade", "Section", "Tuition payments", "Tuition amount",
                  "Book purchases", "Books amount", "Transactions", "Total paid"];
    const rows = visibleStudentTallies.map(r => [
      r.name, r.studentNumber || "",
      r.grade?.name || "", r.section?.name || "",
      String(r.tuitionCount), r.tuitionAmount.toFixed(2),
      String(r.booksCount),   r.booksAmount.toFixed(2),
      String(r.txCount),      r.total.toFixed(2),
    ]);
    const totalRow = ["TOTAL", "", "", "",
      String(studentTotals.tx - visibleStudentTallies.reduce((s, r) => s + r.booksCount, 0)),
      studentTotals.tuition.toFixed(2),
      String(visibleStudentTallies.reduce((s, r) => s + r.booksCount, 0)),
      studentTotals.books.toFixed(2),
      String(studentTotals.tx),
      studentTotals.total.toFixed(2),
    ];
    const csv = [header, [], cols, ...rows, [], totalRow]
      .map(r => r.map(v => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tuition-by-student_${(rangeLabel || "all").replace(/\s+/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className={s.panel}>
      {/* Sub-tabs — student-side money on the left, staff-side on the right.
          The Payroll sub-tab only appears when the Payroll module is on;
          a non-payroll deployment sees just the Tuition & Fees content. */}
      {payrollOn && (
        <div className={s.subTabs} role="tablist">
          <button
            role="tab"
            aria-selected={subTab === "tuition"}
            className={`${s.subTab} ${subTab === "tuition" ? s.subTabActive : ""}`}
            onClick={() => setSubTab("tuition")}>
            Tuition &amp; Fees
          </button>
          <button
            role="tab"
            aria-selected={subTab === "payroll"}
            className={`${s.subTab} ${subTab === "payroll" ? s.subTabActive : ""}`}
            onClick={() => setSubTab("payroll")}>
            Payroll
          </button>
        </div>
      )}

      {err && <div className={s.error}>{err}</div>}

      {loading ? (
        <div className={s.empty}>Loading…</div>
      ) : subTab === "tuition" ? (
        <>
          {/* ── Financial Overview ─────────────────────────────────────── */}
          <section className={s.fovCard}>
            <header className={s.fovHead}>
              <div>
                <div className={s.fovEyebrow}>Financial Overview</div>
                <h2 className={s.fovTitle}>School Financial Summary</h2>
                <p className={s.fovSub}>
                  Grand, Gross, Payments, Expenses &amp; Deposits reflect the selected window — invoices billed, not revenue recognized. Remaining Collectibles is always the live, all-time balance.
                </p>
              </div>
              <div className={s.fovLive} aria-label="Live data">
                <span className={s.fovLiveDot} aria-hidden="true" />
                Live
              </div>
            </header>
            <div className={s.fovGrid}>
              {[
                {
                  key: "grand",
                  label: "Grand Total",
                  value: fovMetrics.grandTotal,
                  desc: "Billed in window · before discounts",
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2"/>
                      <path d="M16 7V5a2 2 0 0 0-4 0v2"/>
                      <line x1="12" y1="12" x2="12" y2="16"/>
                      <line x1="10" y1="14" x2="14" y2="14"/>
                    </svg>
                  ),
                },
                {
                  key: "gross",
                  label: "Gross Total",
                  value: fovMetrics.grossTotal,
                  desc: "Billed in window · after discounts",
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                      <path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
                    </svg>
                  ),
                },
                {
                  key: "pay",
                  label: "Total Payments",
                  value: fovMetrics.totalPayments,
                  desc: "Collected in selected window",
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="5" width="20" height="14" rx="2"/>
                      <line x1="2" y1="10" x2="22" y2="10"/>
                    </svg>
                  ),
                },
                {
                  key: "exp",
                  label: "Total Expenses",
                  value: fovMetrics.totalExpenses,
                  desc: "Recorded in selected window",
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                  ),
                },
                {
                  key: fovMetrics.currentDeposits < -0.005 ? "dep_neg" : "dep",
                  label: "Current Deposits",
                  value: fovMetrics.currentDeposits,
                  desc: "Total payments minus expenses",
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                    </svg>
                  ),
                },
                {
                  key: "col",
                  label: "Remaining Collectibles",
                  value: fovMetrics.remainingCollectibles,
                  desc: "All-time · unaffected by window",
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                  ),
                },
              ].map((tile, i) => (
                <div
                  key={tile.key}
                  className={`${s.fovTile} ${s[`fovTile_${tile.key}`]}`}
                  style={{ animationDelay: `${i * 0.07}s` }}
                >
                  <div className={s.fovTileTop}>
                    <span className={s.fovTileIcon} aria-hidden="true">{tile.icon}</span>
                    <span className={s.fovTileLabel}>{tile.label}</span>
                    <button
                      className={s.fovInfoBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFovOpenInfo({
                          tile,
                          rawData: {
                            invoices,
                            invoicesInRange,
                            payments: paymentsInRange,
                            allPayments: payments,
                            employees,
                            expenses: expensesInRange,
                            feeStructures,
                            sections,
                            students,
                            gradeLevels,
                          },
                          kpis: fovMetrics,
                        });
                      }}
                      aria-label={`Detail for ${tile.label}`}
                      title={`View ${tile.label} breakdown`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 16v-4"/><path d="M12 8h.01"/>
                      </svg>
                    </button>
                  </div>
                  <div className={s.fovTileValue}>{money(tile.value)}</div>
                  <div className={s.fovTileSub}>{tile.desc}</div>
                </div>
              ))}
            </div>
            {fovOpenInfo && (
              <FovInfoModal
                tile={fovOpenInfo.tile}
                onClose={() => setFovOpenInfo(null)}
                rawData={fovOpenInfo.rawData}
                kpis={fovOpenInfo.kpis}
                scopeLabel={range === "custom"
                  ? `${customFrom} to ${customTo}`
                  : RANGES.find(r => r.value === range)?.label}
              />
            )}
          </section>

          {/* Range filter — tuition only; payroll snapshot is window-agnostic.
              When "Custom range…" is picked, two date inputs slide in next
              to the Window selector so the user can pin an arbitrary
              From / To pair. Both inputs are bounded against each other
              via the `min` / `max` attributes so the picker can't go
              backwards in time. */}
          <div className={s.filterRow}>
            <label className={s.filterField}>
              <span>Window</span>
              <select value={range} onChange={e => setRange(e.target.value)}>
                {RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
            {range === "custom" && (
              <>
                <label className={s.filterField}>
                  <span>From</span>
                  <input type="date"
                         value={customFrom}
                         max={customTo || undefined}
                         onChange={e => setCustomFrom(e.target.value)} />
                </label>
                <label className={s.filterField}>
                  <span>To</span>
                  <input type="date"
                         value={customTo}
                         min={customFrom || undefined}
                         max={isoToday(0)}
                         onChange={e => setCustomTo(e.target.value)} />
                </label>
              </>
            )}
            <label className={s.filterField}>
              <span>Filter date by</span>
              <select value={dateField} onChange={e => setDateField(e.target.value)}>
                <option value="received">Received date</option>
                <option value="entered">Date entered in system</option>
              </select>
            </label>
          </div>

          {/* Stat tiles — actual money received in the selected window. No
              estimates, no multiplication, no pretend outflows. */}
          <div className={s.moneyGrid}>
            <MoneyTile kind="in" label="Tuition received" value={tuitionIn}
                       sub={`${paymentsInRange.length} payment${paymentsInRange.length === 1 ? "" : "s"}`}
                       onInfo={paymentsInRange.length > 0 ? openTuitionTile : null} />
            <MoneyTile kind="in" label="Books received"   value={booksIn}
                       sub={`${purchasesInRange.length} purchase${purchasesInRange.length === 1 ? "" : "s"}`}
                       onInfo={purchasesInRange.length > 0 ? openBooksTile : null} />
            <MoneyTile kind="in" label="Total received"   value={totalIn}
                       sub="tuition + books"
                       emphasis
                       onInfo={(paymentsInRange.length + purchasesInRange.length) > 0 ? openTotalTile : null} />
            {payrollOn && (
              <MoneyTile kind="out" label="Configured payroll" value={monthlyPayroll}
                         sub={`${payrollRows.length} staff · see Payroll tab`}
                         onInfo={payrollRows.length > 0 ? openPayrollTile : null} />
            )}
          </div>

          {/* Payment method breakdown — how tuition came in (GCash vs Cash etc.).
              Book purchases don't carry a method today, so this panel covers
              tuition only. */}
          <section className={s.listCard}>
            <header className={s.listHead}>
              <h3>Tuition by payment method</h3>
              <span className={s.listCount}>
                {methodBreakdown.length} method{methodBreakdown.length === 1 ? "" : "s"}
              </span>
            </header>
            {methodBreakdown.length === 0 ? (
              <div className={s.listEmpty}>No tuition payments in this window.</div>
            ) : (
              <div className={s.methodGrid}>
                {methodBreakdown.map(m => {
                  const students = methodStudentBreakdown[m.method] || [];
                  return (
                    <div key={m.method} className={s.methodBlock}>
                      <div className={s.methodBlockHead}>
                        <div className={s.methodBlockTitle}>{m.method}</div>
                        <div className={s.methodBlockTotal}>{money(m.amount)}</div>
                      </div>
                      <div className={s.methodBlockMeta}>
                        {m.count} payment{m.count === 1 ? "" : "s"} ·{" "}
                        {students.length} student{students.length === 1 ? "" : "s"} ·{" "}
                        {(m.share * 100).toFixed(1)}% of tuition
                      </div>
                      {students.length > 0 && (
                        <div className={s.methodTableScroll}>
                          <table className={s.rankTable}>
                            <thead>
                              <tr>
                                <th>Student</th>
                                <th className={s.rankColNum}>Payments</th>
                                <th className={s.rankColMoney}>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {students.map((st, idx) => {
                                const tally = st.studentId != null ? tallyByStudentId.get(st.studentId) : null;
                                return (
                                  <tr key={st.studentId ?? st.name}>
                                    <td>
                                      <span className={s.rankAvatar}
                                            style={{ background: avatarColor(st.name) }}
                                            aria-hidden="true">
                                        {initial(st.name)}
                                      </span>
                                      <span className={s.rankCell}>{idx + 1}</span>
                                      {tally ? (
                                        <button
                                          type="button"
                                          className={s.linkNameBtn}
                                          onClick={() => setInfoStudent(tally)}
                                          aria-label={`Open payment details for ${st.name}`}>
                                          {st.name}
                                        </button>
                                      ) : (
                                        <span>{st.name}</span>
                                      )}
                                    </td>
                                    <td className={s.rankColNum}>{st.count}</td>
                                    <td className={s.rankColMoney}>{money(st.amount)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Per-student tally — combines tuition + book purchases into one
              row per student so the admin can see how much each family has
              paid in total. Click the i icon for a per-student drill-down. */}
          <section className={s.listCard}>
            <header className={s.listHead}>
              <h3>Total paid by student</h3>
              <span className={s.listCount}>
                {visibleStudentTallies.length} student{visibleStudentTallies.length === 1 ? "" : "s"}
              </span>
            </header>
            <div className={s.studentStatStrip}>
              <div className={s.studentStatTile}>
                <div className={s.studentStatLabel}>Top spender</div>
                <div className={s.studentStatValue}>
                  {studentTopSpender ? money(studentTopSpender.total) : "—"}
                </div>
                <div className={s.studentStatSub}>
                  {studentTopSpender ? studentTopSpender.name : "no payments in this window"}
                </div>
              </div>
              <div className={s.studentStatTile}>
                <div className={s.studentStatLabel}>Average per family</div>
                <div className={s.studentStatValue}>{money(studentAvgPerFamily)}</div>
                <div className={s.studentStatSub}>
                  {visibleStudentTallies.length} famil{visibleStudentTallies.length === 1 ? "y" : "ies"} · {studentTotals.tx} transaction{studentTotals.tx === 1 ? "" : "s"}
                </div>
              </div>
            </div>
            <div className={s.studentFilterRow}>
              <input
                type="text"
                className={s.studentSearchInput}
                placeholder="Search name, student #, section…"
                value={studentSearch}
                onChange={e => setStudentSearch(e.target.value)}
              />
              <select
                className={s.studentGradeSelect}
                value={studentGrade}
                onChange={e => setStudentGrade(e.target.value)}>
                <option value="all">All grade levels</option>
                {gradeLevels.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <button type="button" className={s.exportBtn} onClick={exportStudentTalliesCsv}>
                Export CSV
              </button>
            </div>
            {visibleStudentTallies.length === 0 ? (
              <div className={s.listEmpty}>
                No payments from any student in this window.
              </div>
            ) : (
              <div className={s.payrollTableWrap}>
                <table className={s.payrollTable}>
                  <thead>
                    <tr>
                      {[
                        { k: "name",          label: "Student",    align: "left"  },
                        { k: "tuitionAmount", label: "Tuition",    align: "right" },
                        { k: "booksAmount",   label: "Books",      align: "right" },
                        { k: "txCount",       label: "Tx",         align: "right" },
                        { k: "total",         label: "Total paid", align: "right" },
                      ].map(c => {
                        const active = studentSort.key === c.k;
                        const arrow = active ? (studentSort.dir === "asc" ? " ▲" : " ▼") : "";
                        return (
                          <th key={c.k}
                              className={`${c.align === "right" ? s.colRight : ""} ${active ? s.colActive : ""}`}
                              onClick={() => toggleStudentSort(c.k)}>
                            {c.label}{arrow}
                          </th>
                        );
                      })}
                      <th style={{ width: 36 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleStudentTallies.map(r => (
                      <tr key={r.key}>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span className={s.rankAvatar}
                                  style={{ background: avatarColor(r.name) }}
                                  aria-hidden="true">
                              {initial(r.name)}
                            </span>
                            <div style={{ minWidth: 0 }}>
                              <button
                                type="button"
                                className={s.linkNameBtn}
                                onClick={() => setInfoStudent(r)}
                                aria-label={`Open payment details for ${r.name}`}>
                                {r.name}
                              </button>
                              <div style={{ fontSize: 11, color: "#8a8070", marginTop: 1 }}>
                                {[r.studentNumber, r.grade?.name, r.section?.name].filter(Boolean).join(" · ") || "—"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={s.colRight}>{r.tuitionAmount > 0 ? money(r.tuitionAmount) : "—"}</td>
                        <td className={s.colRight}>{r.booksAmount   > 0 ? money(r.booksAmount)   : "—"}</td>
                        <td className={s.colRight}>{r.txCount}</td>
                        <td className={s.colRight} style={{ fontWeight: 800, color: "#4a6d55" }}>
                          {money(r.total)}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <button type="button" className={s.infoBtn}
                                  aria-label="Student payment details"
                                  title="Details"
                                  onClick={() => setInfoStudent(r)}>i</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className={s.colFooterLabel}>TOTAL · {visibleStudentTallies.length} student{visibleStudentTallies.length === 1 ? "" : "s"}</td>
                      <td className={s.colRight}>{money(studentTotals.tuition)}</td>
                      <td className={s.colRight}>{money(studentTotals.books)}</td>
                      <td className={s.colRight}>{studentTotals.tx}</td>
                      <td className={s.colRight} style={{ fontWeight: 800, color: "#4a6d55" }}>
                        {money(studentTotals.total)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>

          {/* Payments ledger — one row per payment (not aggregated), built
              for OR# auditing. Filters: free-text search, method dropdown,
              and a Missing-OR-only toggle for the reconciliation case
              where the cashier needs to find slipped-through payments. */}
          <section className={s.listCard}>
            <header className={s.listHead}>
              <h3>All payments — full ledger</h3>
              <span className={s.listCount}>
                {ledgerTotals.count} payment{ledgerTotals.count === 1 ? "" : "s"}
              </span>
            </header>
            <div className={s.studentStatStrip}>
              <div className={s.studentStatTile}>
                <div className={s.studentStatLabel}>In view</div>
                <div className={s.studentStatValue}>{money(ledgerTotals.amount)}</div>
                <div className={s.studentStatSub}>
                  {ledgerTotals.count} payment{ledgerTotals.count === 1 ? "" : "s"} after filters
                </div>
              </div>
              <div className={`${s.studentStatTile} ${ledgerMissingORCountTotal > 0 ? s.studentStatTileWarn : ""}`}>
                <div className={s.studentStatLabel}>Missing OR&nbsp;#</div>
                <div className={s.studentStatValue}>
                  {ledgerMissingORCountTotal}
                </div>
                <div className={s.studentStatSub}>
                  {ledgerMissingORCountTotal === 0
                    ? "Every payment is receipted"
                    : "Payments without an Official Receipt"}
                </div>
              </div>
            </div>
            <div className={s.ledgerFilterRow}>
              <input
                type="text"
                className={s.studentSearchInput}
                placeholder="Search name, student #, OR #, ref #, particular…"
                value={ledgerSearch}
                onChange={e => setLedgerSearch(e.target.value)}
              />
              <select
                className={s.studentGradeSelect}
                value={ledgerMethod}
                onChange={e => setLedgerMethod(e.target.value)}>
                <option value="all">All methods</option>
                {ledgerMethodOptions.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <button
                type="button"
                className={`${s.ledgerToggle} ${ledgerMissingOR ? s.ledgerToggleActive : ""}`}
                onClick={() => setLedgerMissingOR(v => !v)}
                aria-pressed={ledgerMissingOR}
                title="Show only payments without an OR number — useful for reconciliation">
                Missing OR only
                {ledgerMissingORCountTotal > 0 && (
                  <span className={s.ledgerToggleCount}>{ledgerMissingORCountTotal}</span>
                )}
              </button>
              <button type="button" className={s.exportBtn} onClick={exportLedgerCsv}>
                Export CSV
              </button>
            </div>
            {visibleLedgerRows.length === 0 ? (
              <div className={s.listEmpty}>
                No payments match the current filters.
              </div>
            ) : (
              <div className={s.payrollTableWrap}>
                <table className={s.payrollTable}>
                  <thead>
                    <tr>
                      {[
                        { k: "name",   label: "Student",     align: "left"  },
                        { k: "or",     label: "OR #",        align: "left"  },
                        { k: "method", label: "Method",      align: "left"  },
                        { k: "ref",    label: "Reference #", align: "left"  },
                        { k: "date",   label: "Date Received", align: "left"  },
                        { k: "amount", label: "Amount",      align: "right" },
                      ].map(c => {
                        const active = ledgerSort.key === c.k;
                        const arrow = active ? (ledgerSort.dir === "asc" ? " ▲" : " ▼") : "";
                        return (
                          <th key={c.k}
                              className={`${c.align === "right" ? s.colRight : ""} ${active ? s.colActive : ""}`}
                              onClick={() => toggleLedgerSort(c.k)}>
                            {c.label}{arrow}
                          </th>
                        );
                      })}
                      <th style={{ width: 36 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLedgerRows.slice(0, 500).map(r => {
                      const p = r.payment;
                      const hasTallyForStudent = r.ctx.student?.id != null
                        && tallyByStudentId.get(r.ctx.student.id) != null;
                      const missingOR = !(p.orNumber || "").trim();
                      return (
                        <tr key={p.id} className={missingOR ? s.ledgerRowMissing : ""}>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span className={s.rankAvatar}
                                    style={{ background: avatarColor(r.stName || "—") }}
                                    aria-hidden="true">
                                {initial(r.stName || "—")}
                              </span>
                              <div style={{ minWidth: 0 }}>
                                {hasTallyForStudent ? (
                                  <button
                                    type="button"
                                    className={s.linkNameBtn}
                                    onClick={() => setInfoStudent(tallyByStudentId.get(r.ctx.student.id))}
                                    aria-label={`Open payment details for ${r.stName}`}>
                                    {r.stName || "—"}
                                  </button>
                                ) : (
                                  <span style={{ fontWeight: 600, color: "#1a1a1a" }}>{r.stName || "—"}</span>
                                )}
                                <div style={{ fontSize: 11, color: "#8a8070", marginTop: 1 }}>
                                  {[r.ctx.student?.studentNumber, r.ctx.grade?.name, r.ctx.section?.name]
                                    .filter(Boolean).join(" · ") || "—"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            {missingOR ? (
                              <span className={s.ledgerMissingPill}>—</span>
                            ) : (
                              <span className={s.ledgerOrCell}>{p.orNumber}</span>
                            )}
                          </td>
                          <td>{r.methodLabel || p.method || "—"}</td>
                          <td>{p.referenceNo || "—"}</td>
                          <td>{p.paymentReceivedOn ? formatDate(p.paymentReceivedOn) : (p.paidAt ? formatDateTime(p.paidAt) : "—")}</td>
                          <td className={s.colRight} style={{ fontWeight: 700, color: "#4a6d55" }}>
                            {money(p.amount)}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <button type="button" className={s.infoBtn}
                                    aria-label="Payment details"
                                    title="Details"
                                    onClick={() => setInfoPayment({ payment: p, ...r.ctx })}>i</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className={s.colFooterLabel}>
                        TOTAL · {ledgerTotals.count} payment{ledgerTotals.count === 1 ? "" : "s"}
                      </td>
                      <td colSpan={4} />
                      <td className={s.colRight} style={{ fontWeight: 800, color: "#4a6d55" }}>
                        {money(ledgerTotals.amount)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
                {visibleLedgerRows.length > 500 && (
                  <div className={s.listEmpty} style={{ paddingTop: 10 }}>
                    Showing the first 500 of {visibleLedgerRows.length} matching payments —
                    narrow the filters or use Export CSV for the full set.
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Detail tables */}
          <div className={s.twoColGrid}>
            <section className={s.listCard}>
              <header className={s.listHead}>
                <h3>Recent tuition payments</h3>
                <span className={s.listCount}>{paymentsInRange.length}</span>
              </header>
              {paymentsInRange.length === 0 ? (
                <div className={s.listEmpty}>No payments in this window.</div>
              ) : (
                <>
                  <SortBar
                    label="Sort by"
                    sort={paymentSort}
                    onToggle={togglePaymentSort}
                    options={[
                      { k: "date",   label: "Date"    },
                      { k: "amount", label: "Amount"  },
                      { k: "name",   label: "Student" },
                      { k: "or",     label: "OR #"    },
                    ]}
                    s={s}
                  />
                  <ul className={s.list}>
                    {sortedPaymentsInRange.slice(0, 25).map(p => {
                      const ctx = paymentContext(p);
                      const { student, section, grade, processor } = ctx;
                      const title = student
                        ? `${student.firstName} ${student.lastName}`
                        : (p.studentName || "—");
                      const metaParts = [
                        grade?.name,
                        section?.name,
                        p.orNumber ? `OR ${p.orNumber}` : null,
                        p.method,
                        effectivePaymentDateLabel(p),
                        processor?.name ? `by ${processor.name}` : null,
                      ].filter(Boolean);
                      return (
                        <li key={p.id} className={s.listRow}>
                          <div className={s.listRowMain}>
                            <button
                              type="button"
                              className={`${s.linkNameBtn} ${s.listRowTitle}`}
                              onClick={() => setInfoPayment({ payment: p, ...ctx })}
                              aria-label={`Open payment details for ${title}`}>
                              {title}
                            </button>
                            <div className={s.listRowMeta}>{metaParts.join(" · ")}</div>
                          </div>
                          <div className={s.listRowRight}>
                            <div className={s.listRowValueIn}>{money(p.amount)}</div>
                            <button type="button" className={s.infoBtn}
                                    aria-label="Payment details"
                                    title="Details"
                                    onClick={() => setInfoPayment({ payment: p, ...ctx })}>
                              i
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </section>

            <section className={s.listCard}>
              <header className={s.listHead}>
                <h3>Recent book purchases</h3>
                <span className={s.listCount}>{purchasesInRange.length}</span>
              </header>
              {purchasesInRange.length === 0 ? (
                <div className={s.listEmpty}>No book purchases in this window.</div>
              ) : (
                <>
                  <SortBar
                    label="Sort by"
                    sort={purchaseSort}
                    onToggle={togglePurchaseSort}
                    options={[
                      { k: "date",   label: "Date"    },
                      { k: "amount", label: "Amount"  },
                      { k: "title",  label: "Book"    },
                      { k: "name",   label: "Student" },
                    ]}
                    s={s}
                  />
                  <ul className={s.list}>
                    {sortedPurchasesInRange.slice(0, 25).map(p => {
                      const book = bookById[p.bookId];
                      const stu  = studentById[p.studentId];
                      const stName = stu ? `${stu.firstName} ${stu.lastName}` : null;
                      const tally = stu ? tallyByStudentId.get(stu.id) : null;
                      return (
                        <li key={p.id} className={s.listRow}>
                          <div className={s.listRowMain}>
                            <div className={s.listRowTitle}>
                              {book?.title || "(Unknown book)"}
                            </div>
                            <div className={s.listRowMeta}>
                              {stName && tally ? (
                                <>
                                  <button
                                    type="button"
                                    className={s.linkNameBtn}
                                    onClick={() => setInfoStudent(tally)}
                                    aria-label={`Open payment details for ${stName}`}>
                                    {stName}
                                  </button>
                                  {" · "}
                                </>
                              ) : stName ? `${stName} · ` : ""}
                              {formatDate(p.purchasedAt)}
                            </div>
                          </div>
                          <div className={s.listRowValueIn}>{money(p.amount)}</div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </section>
          </div>

        </>
      ) : (
        <PayrollReport
          employees={employees}
          monthlyPayroll={monthlyPayroll}
          payrollRows={payrollRows}
          tuitionIn={tuitionIn}
          tuitionRangeLabel={(range === "custom"
            ? customRangeLabel(customFrom, customTo)
            : (RANGES.find(r => r.value === range)?.label || "current window")
          ).toLowerCase()}
        />
      )}

      {/* Order matters: StudentDetailsModal is rendered FIRST so it lands
          below the PaymentDetailsModal in DOM stacking order. When a user
          clicks the `i` on a payment row inside the student modal, we
          leave the student modal mounted and stack the payment modal on
          top — closing payment reveals the still-open student behind it,
          so the user doesn't have to re-navigate from the table. */}
      {infoStudent && (
        <StudentDetailsModal
          row={infoStudent}
          bookById={bookById}
          onClose={() => setInfoStudent(null)}
          onPaymentInfo={p => {
            const ctx = paymentContext(p);
            setInfoPayment({ payment: p, ...ctx });
          }}
          s={s}
        />
      )}

      {infoPayment && (
        <PaymentDetailsModal
          info={infoPayment}
          onClose={() => setInfoPayment(null)}
          s={s}
        />
      )}

      {tileInfo && (
        <TilePaymentsModal
          info={tileInfo}
          onClose={() => setTileInfo(null)}
        />
      )}
    </div>
  );
}

function PaymentDetailsModal({ info, onClose, s }) {
  const { payment: p, invoice, student, section, grade, processor } = info;
  const enteredFull = p.paidAt
    ? new Date(p.paidAt).toLocaleString(undefined,
        { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";
  const receivedFull = p.paymentReceivedOn ? formatDate(p.paymentReceivedOn) : "—";
  const rows = [
    ["Student",        student ? `${student.firstName} ${student.lastName}` : "—"],
    ["Student #",      student?.studentNumber || "—"],
    ["Grade level",    grade?.name   || "—"],
    ["Section",        section?.name || "—"],
    ["Invoice",        invoice ? `#${invoice.id} · ${money(invoice.amount)}` : "—"],
    ["OR #",           p.orNumber || "—"],
    ["Amount paid",    money(p.amount)],
    ["Method",         p.method || "—"],
    ["Bank",           p.bankName || "—"],
    ["Reference #",    p.referenceNo || "—"],
    ["Particular",     p.particular || "—"],
    ["Date received",  receivedFull],
    ["Entered",        enteredFull],
    ["Processed by",   processor?.name || (p.receivedByStaffId ? `Staff #${p.receivedByStaffId}` : "—")],
    ["Notes",          p.notes || "—"],
  ];
  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  return createPortal(
    <div className={s.modalOverlay}>
      <div className={s.modal} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>Payment details</h3>
          <button type="button" className={s.modalClose}
                  onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className={s.detailsGrid}>
          {rows.map(([label, value]) => (
            <div key={label} className={s.detailsRow}>
              <span className={s.detailsLabel}>{label}</span>
              <span className={s.detailsValue}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Per-student drill-down modal — every tuition payment + every book
 *  purchase for the chosen student in the active window, with totals.
 *  The "i" on each tuition row hops back through to the existing
 *  PaymentDetailsModal so the admin doesn't lose the broader context. */
function StudentDetailsModal({ row, bookById, onClose, onPaymentInfo, s }) {
  // Local sort state for the per-student Tuition payments list. Default
  // "date desc" preserves the prior visible ordering. State is scoped to
  // this modal instance, so opening a different student starts fresh —
  // and toggling sort here can never affect any other panel on the tab.
  const [paySort, setPaySort] = useState({ key: "date", dir: "desc" });
  const togglePaySort = (key) => setPaySort(prev => prev.key === key
    ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
    : { key, dir: (key === "or" || key === "method") ? "asc" : "desc" });

  const payments = useMemo(() => {
    const list = row.payments.slice();
    const dir = paySort.dir === "asc" ? 1 : -1;
    const keyOf = (p) => {
      if (paySort.key === "date")   return effectivePaymentTs(p);
      if (paySort.key === "amount") return Number(p.amount || 0);
      if (paySort.key === "or")     return String(p.orNumber || "").toLowerCase();
      if (paySort.key === "method") {
        // BANK · BPI sorts together with BANK · BDO under "B" — the
        // method label is the natural compound key the user sees.
        const m = (p.method || "").toUpperCase();
        const b = (p.bankName || "").trim();
        return m === "BANK" && b ? `BANK ${b.toLowerCase()}` : m.toLowerCase();
      }
      return 0;
    };
    list.sort((a, b) => {
      const ka = keyOf(a), kb = keyOf(b);
      if (typeof ka === "string") return ka.localeCompare(kb) * dir;
      return (ka - kb) * dir;
    });
    return list;
  }, [row.payments, paySort]);

  const purchases = useMemo(() => row.purchases.slice().sort((a, b) =>
    new Date(b.purchasedAt || 0).getTime() - new Date(a.purchasedAt || 0).getTime()
  ), [row.purchases]);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  return createPortal(
    <div className={s.modalOverlay}>
      <div className={`${s.modal} ${s.modalWide}`} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>{row.name}</h3>
          <button type="button" className={s.modalClose}
                  onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className={s.detailsGrid}>
          <div className={s.detailsRow}>
            <span className={s.detailsLabel}>Student #</span>
            <span className={s.detailsValue}>{row.studentNumber || "—"}</span>
          </div>
          <div className={s.detailsRow}>
            <span className={s.detailsLabel}>Grade</span>
            <span className={s.detailsValue}>{row.grade?.name || "—"}</span>
          </div>
          <div className={s.detailsRow}>
            <span className={s.detailsLabel}>Section</span>
            <span className={s.detailsValue}>{row.section?.name || "—"}</span>
          </div>
          <div className={s.detailsRow}>
            <span className={s.detailsLabel}>Tuition paid</span>
            <span className={s.detailsValue}>
              {row.tuitionCount} payment{row.tuitionCount === 1 ? "" : "s"} · <strong>{money(row.tuitionAmount)}</strong>
            </span>
          </div>
          <div className={s.detailsRow}>
            <span className={s.detailsLabel}>Books</span>
            <span className={s.detailsValue}>
              {row.booksCount} purchase{row.booksCount === 1 ? "" : "s"} · <strong>{money(row.booksAmount)}</strong>
            </span>
          </div>
          <div className={s.detailsRow}>
            <span className={s.detailsLabel}>Total paid</span>
            <span className={s.detailsValue} style={{ fontSize: "1.05rem", fontWeight: 800, color: "#4a6d55" }}>
              {money(row.total)}
            </span>
          </div>
        </div>

        <div className={s.studentDetailSplit}>
          <section className={s.listCard}>
            <header className={s.listHead}>
              <h3>Tuition payments</h3>
              <span className={s.listCount}>{payments.length}</span>
            </header>
            {payments.length === 0 ? (
              <div className={s.listEmpty}>No tuition payments in this window.</div>
            ) : (
              <>
                <SortBar
                  label="Sort by"
                  sort={paySort}
                  onToggle={togglePaySort}
                  options={[
                    { k: "date",   label: "Date"   },
                    { k: "amount", label: "Amount" },
                    { k: "or",     label: "OR #"   },
                    { k: "method", label: "Method" },
                  ]}
                  s={s}
                />
                <ul className={s.list}>
                  {payments.map(p => (
                    <li key={`pay-${p.id}`} className={s.listRow}>
                    <div className={s.listRowMain}>
                      <div className={s.listRowTitle}>
                        {p.method || "—"}
                        {p.bankName ? ` · ${p.bankName}` : ""}
                        {p.orNumber ? ` · OR ${p.orNumber}` : ""}
                        {p.referenceNo ? ` · ref ${p.referenceNo}` : ""}
                      </div>
                      <div className={s.listRowMeta}>
                        {effectivePaymentDateLabel(p)}
                        {p.particular ? ` · ${p.particular}` : ""}
                      </div>
                    </div>
                    <div className={s.listRowRight}>
                      <div className={s.listRowValueIn}>{money(p.amount)}</div>
                      <button type="button" className={s.infoBtn}
                              aria-label="Payment details"
                              title="Details"
                              onClick={() => onPaymentInfo(p)}>i</button>
                    </div>
                  </li>
                ))}
              </ul>
              </>
            )}
          </section>

          <section className={s.listCard}>
            <header className={s.listHead}>
              <h3>Book purchases</h3>
              <span className={s.listCount}>{purchases.length}</span>
            </header>
            {purchases.length === 0 ? (
              <div className={s.listEmpty}>No book purchases in this window.</div>
            ) : (
              <ul className={s.list}>
                {purchases.map(p => {
                  const book = bookById[p.bookId];
                  return (
                    <li key={`buy-${p.id}`} className={s.listRow}>
                      <div className={s.listRowMain}>
                        <div className={s.listRowTitle}>{book?.title || "(Unknown book)"}</div>
                        <div className={s.listRowMeta}>{formatDate(p.purchasedAt)}</div>
                      </div>
                      <div className={s.listRowValueIn}>{money(p.amount)}</div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
}

function MoneyTile({ kind, label, value, sub, emphasis, onInfo }) {
  const display = value == null ? "—" : money(value);
  return (
    <div className={`${s.moneyTile} ${s[`moneyTile_${kind}`]} ${emphasis ? s.moneyTileEmph : ""}`}>
      <div className={s.moneyTileHead}>
        <div className={s.moneyLabel}>{label}</div>
        {onInfo && (
          <button type="button" className={s.tileInfoBtn} onClick={onInfo}
                  aria-label={`View ${label} details`} title="View details">i</button>
        )}
      </div>
      <div className={s.moneyValue}>{display}</div>
      {sub && <div className={s.moneySub}>{sub}</div>}
    </div>
  );
}

function TilePaymentsModal({ info, onClose }) {
  const isTuition = info.type === "tuition";
  const isBooks   = info.type === "books";
  const isTotal   = info.type === "total";
  const isPayroll = info.type === "payroll";

  const defaultKey = isTuition ? "dateRec" : isPayroll ? "salary" : "date";
  const [sort, setSort] = useState({ key: defaultKey, dir: "desc" });
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  function toggleSort(key) {
    const textKeys = ["student", "method", "grade", "book", "kind", "name", "role", "status"];
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: textKeys.includes(key) ? "asc" : "desc" });
  }

  const stuLabel = r => {
    const stu = r.student;
    if (!stu) return "—";
    return `${stu.firstName || ""} ${stu.lastName || ""}`.trim() || `#${stu.id}`;
  };

  const empLabel = r => r.name || `${r.firstName || ""} ${r.lastName || ""}`.trim() || `#${r.id}`;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return info.rows;
    return info.rows.filter(r => {
      if (isPayroll) {
        return (
          empLabel(r).toLowerCase().includes(q) ||
          (r.role || "").toLowerCase().includes(q) ||
          (r.department || "").toLowerCase().includes(q) ||
          (r.status || "").toLowerCase().includes(q)
        );
      }
      const name = stuLabel(r).toLowerCase();
      if (isTuition) return (
        name.includes(q) ||
        String(r.orNumber || "").toLowerCase().includes(q) ||
        (r.method || "").toLowerCase().includes(q) ||
        (r.grade?.name || "").toLowerCase().includes(q)
      );
      if (isBooks) return (
        name.includes(q) ||
        (r.book?.title || "").toLowerCase().includes(q) ||
        (r.grade?.name || "").toLowerCase().includes(q)
      );
      return (
        name.includes(q) ||
        String(r.orNumber || r.book?.title || "").toLowerCase().includes(q) ||
        (r.grade?.name || "").toLowerCase().includes(q) ||
        (r._kind || "").toLowerCase().includes(q)
      );
    });
  }, [info.rows, search, isTuition, isBooks, isPayroll]);

  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    return filtered.slice().sort((a, b) => {
      const keyOf = r => {
        if (sort.key === "dateRec") return r.paymentReceivedOn ? new Date(r.paymentReceivedOn).getTime() : 0;
        if (sort.key === "dateIn")  return r.paidAt ? new Date(r.paidAt).getTime() : 0;
        if (sort.key === "date")    return new Date(r.purchasedAt || r._date || 0).getTime();
        if (sort.key === "amount")  return Number(r.amount || 0);
        if (sort.key === "salary")  return Number(r.salary || 0);
        if (sort.key === "student") return `${r.student?.lastName || ""} ${r.student?.firstName || ""}`.trim().toLowerCase();
        if (sort.key === "name")    return empLabel(r).toLowerCase();
        if (sort.key === "or")      return String(r.orNumber || "").toLowerCase();
        if (sort.key === "method")  return (r.method || "").toLowerCase();
        if (sort.key === "grade")   return (r.grade?.name || "").toLowerCase();
        if (sort.key === "book")    return (r.book?.title || "").toLowerCase();
        if (sort.key === "kind")    return (r._kind || "").toLowerCase();
        if (sort.key === "role")    return (r.role || "").toLowerCase();
        if (sort.key === "status")  return (r.status || "").toLowerCase();
        return 0;
      };
      const ka = keyOf(a), kb = keyOf(b);
      if (typeof ka === "string") return ka.localeCompare(kb) * dir;
      return (ka - kb) * dir;
    });
  }, [filtered, sort]);

  const visibleTotal = sorted.reduce(
    (sum, r) => sum + Number(isPayroll ? (r.salary || 0) : (r.amount || 0)), 0
  );

  const COLS       = { tuition: 8, books: 5, total: 6, payroll: 4 };
  const LABEL_SPAN = { tuition: 5, books: 3, total: 4, payroll: 3 };
  const EYEBROW    = { tuition: "TUITION RECEIVED", books: "BOOKS RECEIVED",
                       total: "TOTAL RECEIVED",    payroll: "CONFIGURED PAYROLL" };
  const TITLE      = { tuition: "Tuition Payments", books: "Book Purchases",
                       total: "All Received Payments", payroll: "Staff Salary Breakdown" };
  const HINT       = { tuition: "Search student, OR #, method, grade…",
                       books:   "Search book, student, grade…",
                       total:   "Search student, OR #, book, grade, type…",
                       payroll: "Search name, role, department…" };
  const t = info.type;

  function Th({ col, label, align }) {
    const active = sort.key === col;
    const arrow = active ? (sort.dir === "asc" ? " ▲" : " ▼") : "";
    return (
      <th className={`${s.tileModalTh} ${s.tileModalThSort} ${align === "right" ? s.tileModalThRight : ""}`}
          onClick={() => toggleSort(col)}>
        {label}{arrow}
      </th>
    );
  }

  return createPortal(
    <div className={s.modalOverlay}>
      <div className={`${s.modal} ${s.tileModal}`} role="dialog" aria-modal="true"
           onClick={e => e.stopPropagation()}>

        <div className={s.modalHead}>
          <div>
            <div className={s.tileModalEyebrow} style={{ color: isPayroll ? "#b06628" : "#6b8f6b" }}>
              {EYEBROW[t]}
            </div>
            <h3 className={s.modalTitle}>{TITLE[t]} · {money(info.total)}</h3>
          </div>
          <button type="button" className={s.modalClose} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className={s.tileModalSearch}>
          <input
            type="search"
            className={s.tileModalSearchInput}
            placeholder={HINT[t]}
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search"
          />
          <span className={s.tileModalCount}>
            {filtered.length} of {info.rows.length} record{info.rows.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className={s.tileModalTableWrap}>
          <table className={s.tileModalTable}>
            <thead>
              <tr>
                {isTuition && (<>
                  <Th col="or"      label="OR #" />
                  <Th col="student" label="Student" />
                  <Th col="grade"   label="Grade" />
                  <th className={s.tileModalTh}>Method</th>
                  <th className={s.tileModalTh}>Reference</th>
                  <Th col="amount"  label="Amount"               align="right" />
                  <Th col="dateRec" label="Date Received" />
                  <Th col="dateIn"  label="Date Recorded (Input)" />
                </>)}
                {isBooks && (<>
                  <Th col="book"    label="Book" />
                  <Th col="student" label="Student" />
                  <Th col="grade"   label="Grade" />
                  <Th col="amount"  label="Amount" align="right" />
                  <Th col="date"    label="Date Purchased" />
                </>)}
                {isTotal && (<>
                  <Th col="kind"    label="Type" />
                  <th className={s.tileModalTh}>Reference</th>
                  <Th col="student" label="Student" />
                  <Th col="grade"   label="Grade" />
                  <Th col="amount"  label="Amount" align="right" />
                  <Th col="date"    label="Date" />
                </>)}
                {isPayroll && (<>
                  <Th col="name"   label="Employee" />
                  <Th col="role"   label="Role" />
                  <Th col="status" label="Status" />
                  <Th col="salary" label="Monthly Salary" align="right" />
                </>)}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={COLS[t]} className={s.tileModalEmpty}>
                    No records match your search.
                  </td>
                </tr>
              ) : sorted.map((r, i) => (
                <tr key={r.id || i} className={s.tileModalTr}>
                  {isTuition && (<>
                    <td className={`${s.tileModalTd} ${s.tileModalMono}`}>
                      {r.orNumber || <span className={s.tileModalMuted}>—</span>}
                    </td>
                    <td className={s.tileModalTd}>{stuLabel(r)}</td>
                    <td className={s.tileModalTd}>{r.grade?.name || <span className={s.tileModalMuted}>—</span>}</td>
                    <td className={s.tileModalTd}>{r.method || <span className={s.tileModalMuted}>—</span>}</td>
                    <td className={`${s.tileModalTd} ${s.tileModalMono}`}>
                      {r.bankName || r.referenceNo || <span className={s.tileModalMuted}>—</span>}
                    </td>
                    <td className={`${s.tileModalTd} ${s.tileModalAmt}`}>{money(r.amount)}</td>
                    <td className={s.tileModalTd}>
                      {r.paymentReceivedOn
                        ? <span className={s.tileModalDateBadge}>{formatDate(r.paymentReceivedOn)}</span>
                        : <span className={s.tileModalMuted}>—</span>}
                    </td>
                    <td className={s.tileModalTd}>
                      {r.paidAt
                        ? <span className={s.tileModalDateAlt}>{formatDateTime(r.paidAt)}</span>
                        : <span className={s.tileModalMuted}>—</span>}
                    </td>
                  </>)}
                  {isBooks && (<>
                    <td className={s.tileModalTd}>{r.book?.title || <span className={s.tileModalMuted}>(Unknown book)</span>}</td>
                    <td className={s.tileModalTd}>{stuLabel(r)}</td>
                    <td className={s.tileModalTd}>{r.grade?.name || <span className={s.tileModalMuted}>—</span>}</td>
                    <td className={`${s.tileModalTd} ${s.tileModalAmt}`}>{money(r.amount)}</td>
                    <td className={s.tileModalTd}>{formatDate(r.purchasedAt)}</td>
                  </>)}
                  {isTotal && (<>
                    <td className={s.tileModalTd}>
                      <span className={`${s.tileKindBadge} ${r._kind === "Tuition" ? s.tileKindTuition : s.tileKindBook}`}>
                        {r._kind}
                      </span>
                    </td>
                    <td className={`${s.tileModalTd} ${s.tileModalMono}`}>
                      {r._kind === "Tuition"
                        ? (r.orNumber || <span className={s.tileModalMuted}>—</span>)
                        : (r.book?.title || <span className={s.tileModalMuted}>—</span>)}
                    </td>
                    <td className={s.tileModalTd}>{stuLabel(r)}</td>
                    <td className={s.tileModalTd}>{r.grade?.name || <span className={s.tileModalMuted}>—</span>}</td>
                    <td className={`${s.tileModalTd} ${s.tileModalAmt}`}>{money(r.amount)}</td>
                    <td className={s.tileModalTd}>
                      {r._date
                        ? <span className={s.tileModalDateBadge}>{formatDate(r._date)}</span>
                        : <span className={s.tileModalMuted}>—</span>}
                    </td>
                  </>)}
                  {isPayroll && (<>
                    <td className={s.tileModalTd} style={{ fontWeight: 600 }}>{empLabel(r)}</td>
                    <td className={s.tileModalTd}>{r.role || <span className={s.tileModalMuted}>—</span>}</td>
                    <td className={s.tileModalTd}>
                      <span className={`${s.tileStatusBadge} ${(r.status || "Active") === "Inactive" ? s.tileStatusInactive : s.tileStatusActive}`}>
                        {r.status || "Active"}
                      </span>
                    </td>
                    <td className={`${s.tileModalTd} ${s.tileModalAmt}`} style={{ color: "#b06628" }}>
                      {money(r.salary)}
                    </td>
                  </>)}
                </tr>
              ))}
            </tbody>
            {sorted.length > 0 && (
              <tfoot className={s.tileModalTfoot}>
                <tr>
                  <td colSpan={LABEL_SPAN[t]} className={s.tileModalFootLabel}>
                    TOTAL · {sorted.length} record{sorted.length !== 1 ? "s" : ""}
                  </td>
                  <td className={`${s.tileModalTd} ${s.tileModalAmt} ${s.tileModalFootAmt}`}
                      style={isPayroll ? { color: "#b06628" } : undefined}>
                    {money(visibleTotal)}
                  </td>
                  {isTuition && <td colSpan={2} />}
                  {(isBooks || isTotal) && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Click-to-sort chip strip — used above each Recent list. One chip per
 *  sortable key, active chip carries the arrow + bold treatment, click
 *  toggles asc/desc on the same key. Receives the parent's CSS-module so
 *  the styling stays consistent with the rest of the tab. */
function SortBar({ label, sort, onToggle, options, s }) {
  return (
    <div className={s.sortBar} role="toolbar" aria-label="Sort options">
      <span className={s.sortBarLabel}>{label}</span>
      {options.map(o => {
        const active = sort.key === o.k;
        const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "";
        return (
          <button
            key={o.k}
            type="button"
            className={`${s.sortChip} ${active ? s.sortChipActive : ""}`}
            onClick={() => onToggle(o.k)}
            aria-pressed={active}
            aria-label={`Sort by ${o.label}${active ? `, currently ${sort.dir}ending` : ""}`}>
            {o.label}{arrow ? " " + arrow : ""}
          </button>
        );
      })}
    </div>
  );
}
